// 14 Advanced + System feature surfaces. Each page is small + focused — table
// for list-style features, settings form for singleton features. All hit the
// /api/advanced/* surface defined in routes/advanced_system.js.

import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Save, Power, Search, ShieldAlert, Network, Globe, Phone, Music, FileText, Settings as SettingsIcon, Activity, Edit2, Upload, Download, Rocket, ArrowUp, RotateCcw } from 'lucide-react';
import { api, getToken } from '../lib/api';
import { useToast } from '../components/Toast';
// parseCsv / serializeCsv no longer needed here — export/import use server endpoints

// Defensive normaliser — useQuery's data destructure default only kicks in
// when data is undefined. If the API returns `{error: ...}` or null, the
// destructure passes that through and downstream `.map()` crashes the tree.
function asArray<T = any>(v: any): T[] {
  if (Array.isArray(v))             return v as T[];
  if (Array.isArray(v?.rows))       return v.rows as T[];
  if (Array.isArray(v?.data))       return v.data as T[];
  return [];
}

// ────────────────────────────────────────────────────────────────────────────
// IP Blacklist — 3CX shape: Add/Edit/Delete/Import/Export toolbar + search +
// table (IP / Subnet / Action / Expires / Range / Description) + pagination.
// ────────────────────────────────────────────────────────────────────────────
type IpRow = {
  entry_uuid: string;
  ip_address: string;
  subnet_mask?: string;
  action: 'allow' | 'deny';
  expires_at?: string;
  ip_range?: string;
  description?: string;
};

export function IPBlacklist() {
  const qc = useQueryClient();
  const list: IpRow[] = asArray<IpRow>(useQuery<any>({ queryKey: ['ip-blacklist'], queryFn: () => api.advanced.ipBlacklistList() }).data);
  const [search, setSearch]     = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [editorRow, setEditorRow] = useState<IpRow | null | undefined>(undefined); // undefined=closed, null=new
  const [page, setPage]         = useState(1);
  const pageSize = 25;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (list || []).filter(r =>
      !q || `${r.ip_address} ${r.subnet_mask || ''} ${r.description || ''}`.toLowerCase().includes(q)
    );
  }, [list, search]);
  const pages   = Math.max(1, Math.ceil(filtered.length / pageSize));
  const visible = filtered.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => { setPage(1); }, [search]);

  const remove = useMutation({
    mutationFn: (ids: string[]) => Promise.all(ids.map(id => api.advanced.ipBlacklistRemove(id))),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ip-blacklist'] }); setSelected(new Set()); },
  });

  function toggle(id: string) {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  }
  function toggleAll() {
    if (selected.size === visible.length) setSelected(new Set());
    else setSelected(new Set(visible.map(r => r.entry_uuid)));
  }

  function exportCsv() {
    const head = 'IP Address,Subnet Mask,Action,Expiration Date,IP Address Range,Description\r\n';
    const esc = (v: any) => `"${String(v ?? '').replaceAll('"','""')}"`;
    const body = filtered.map(r => [
      esc(r.ip_address), esc(r.subnet_mask || ''), esc(r.action),
      esc(r.expires_at ? new Date(r.expires_at).toISOString() : ''),
      esc(r.ip_range || ''), esc(r.description || ''),
    ].join(',')).join('\r\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([head + body], { type: 'text/csv' }));
    a.download = `ip-blacklist-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }
  function importCsv() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv,text/csv';
    input.onchange = async () => {
      const f = input.files?.[0];
      if (!f) return;
      const text = await f.text();
      const lines = text.split(/\r?\n/).filter(Boolean).slice(1); // drop header
      let n = 0;
      for (const line of lines) {
        const cells = line.match(/"([^"]*)"|[^,]+/g)?.map((c: string) => c.replace(/^"|"$/g, '')) || [];
        const [ip, subnet, action, expires, , description] = cells;
        if (!ip) continue;
        await api.advanced.ipBlacklistAdd({
          ip_address: ip,
          subnet_mask: subnet || '',
          action: (action || 'deny').toLowerCase(),
          expires_at: expires || null,
          description: description || '',
        });
        n++;
      }
      qc.invalidateQueries({ queryKey: ['ip-blacklist'] });
      alert(`Imported ${n} entries.`);
    };
    input.click();
  }

  return (
    <div className="space-y-3">
      <h1 className="text-xl font-semibold">IP Blacklist</h1>

      {/* Toolbar — primary blue 5 buttons */}
      <div className="card p-2 flex flex-wrap gap-1 items-center">
        <button className="btn-primary text-sm inline-flex items-center gap-1" onClick={() => setEditorRow(null)}>
          <Plus size={14} /> Add
        </button>
        <button className="btn-primary text-sm inline-flex items-center gap-1"
                onClick={() => {
                  if (selected.size !== 1) return alert('Select exactly one row to edit.');
                  setEditorRow(filtered.find(r => r.entry_uuid === [...selected][0]) || null);
                }}>
          <Edit2 size={14} /> Edit
        </button>
        <button className="btn-primary text-sm inline-flex items-center gap-1"
                onClick={() => {
                  if (!selected.size) return alert('Select rows to delete.');
                  if (confirm(`Delete ${selected.size} entry(ies)?`)) remove.mutate([...selected]);
                }}>
          <Trash2 size={14} /> Delete
        </button>
        <button className="btn-primary text-sm inline-flex items-center gap-1" onClick={importCsv}>
          <Upload size={14} /> Import
        </button>
        <button className="btn-primary text-sm inline-flex items-center gap-1" onClick={exportCsv}>
          <Download size={14} /> Export
        </button>
      </div>

      <div className="flex items-center gap-1 bg-elevated rounded-md px-2 py-1.5">
        <Search size={14} className="text-textSecondary" />
        <input className="bg-transparent outline-none text-sm w-full"
               value={search} onChange={e => setSearch(e.target.value)}
               placeholder="Search…" />
      </div>

      <div className="card p-0 overflow-x-auto">
        <table className="table">
          <thead>
            <tr>
              <th className="w-8">
                <input type="checkbox"
                       checked={visible.length > 0 && selected.size === visible.length}
                       onChange={toggleAll} />
              </th>
              <th>IP Address</th>
              <th>Subnet Mask</th>
              <th className="w-24">Action</th>
              <th className="w-44">Expiration Date</th>
              <th>IP Address Range</th>
              <th>Description</th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {visible.map(r => (
              <tr key={r.entry_uuid} className={`hover:bg-elevated ${selected.has(r.entry_uuid) ? 'bg-primary/5' : ''}`}>
                <td onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" checked={selected.has(r.entry_uuid)} onChange={() => toggle(r.entry_uuid)} />
                </td>
                <td className="font-mono text-sm">{r.ip_address}</td>
                <td className="text-sm">{r.subnet_mask || ''}</td>
                <td className={`text-sm ${r.action === 'allow' ? 'text-success' : 'text-danger'}`}>
                  {r.action === 'allow' ? 'Allow' : 'Deny'}
                </td>
                <td className="text-sm">{r.expires_at ? formatStamp3cx(r.expires_at) : ''}</td>
                <td className="font-mono text-sm">{r.ip_range || r.ip_address}</td>
                <td className="text-sm">{r.description}</td>
                <td>
                  <button className="btn-ghost p-1" title="Edit" onClick={() => setEditorRow(r)}>
                    <Edit2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {!visible.length && (
              <tr><td colSpan={8} className="text-center py-8 text-textSecondary">No entries.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {pages > 1 && <SimplePagination page={page} pages={pages} onPage={setPage} />}

      {editorRow !== undefined && (
        <IpBlacklistEditor
          row={editorRow}
          onClose={() => setEditorRow(undefined)}
          onSaved={() => { qc.invalidateQueries({ queryKey: ['ip-blacklist'] }); setEditorRow(undefined); }}
        />
      )}
    </div>
  );
}

function IpBlacklistEditor({ row, onClose, onSaved }: { row: IpRow | null; onClose: () => void; onSaved: () => void }) {
  const [ip, setIp]       = useState(row?.ip_address || '');
  const [subnet, setSubnet] = useState(row?.subnet_mask || '');
  const [action, setAction] = useState<'allow' | 'deny'>(row?.action || 'deny');
  const [expires, setExpires] = useState(row?.expires_at ? row.expires_at.slice(0, 10) : '');
  const [description, setDescription] = useState(row?.description || '');
  const [busy, setBusy] = useState(false);
  const [err,  setErr]  = useState('');

  async function go() {
    if (!ip) { setErr('IP Address required'); return; }
    setBusy(true); setErr('');
    try {
      const payload = { ip_address: ip, subnet_mask: subnet, action, expires_at: expires || null, description };
      if (row) await api.advanced.ipBlacklistUpdate?.(row.entry_uuid, payload);
      else      await api.advanced.ipBlacklistAdd(payload);
      onSaved();
    } catch (e: any) { setErr(String(e.message || e)); }
    finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center" onClick={onClose}>
      <div className="card p-4 w-full max-w-lg" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-semibold mb-3">{row ? 'Edit IP entry' : 'Add IP entry'}</h2>
        <div className="grid grid-cols-2 gap-3">
          <Field label="IP Address *"><input className="input font-mono" value={ip} onChange={e => setIp(e.target.value)} placeholder="1.2.3.4" /></Field>
          <Field label="Subnet Mask"><input className="input font-mono" value={subnet} onChange={e => setSubnet(e.target.value)} placeholder="/32 or 255.255.255.0" /></Field>
          <Field label="Action">
            <select className="input" value={action} onChange={e => setAction(e.target.value as any)}>
              <option value="allow">Allow</option>
              <option value="deny">Deny</option>
            </select>
          </Field>
          <Field label="Expiration Date"><input className="input" type="date" value={expires} onChange={e => setExpires(e.target.value)} /></Field>
          <div className="col-span-2">
            <Field label="Description"><input className="input" value={description} onChange={e => setDescription(e.target.value)} /></Field>
          </div>
        </div>
        {err && <div className="text-sm text-danger mt-2">{err}</div>}
        <div className="flex justify-end gap-2 mt-4">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={busy} onClick={go}>{busy ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

function formatStamp3cx(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const mm   = String(d.getMonth() + 1).padStart(2, '0');
  const dd   = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  let h = d.getHours();
  const ap = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${mm}/${dd}/${yyyy} ${String(h).padStart(2,'0')}:${min} ${ap}`;
}

function SimplePagination({ page, pages, onPage }: { page: number; pages: number; onPage: (n: number) => void }) {
  const nums: (number | '…')[] = [];
  if (pages <= 7) for (let i = 1; i <= pages; i++) nums.push(i);
  else {
    nums.push(1);
    if (page > 3) nums.push('…');
    for (let i = Math.max(2, page - 1); i <= Math.min(pages - 1, page + 1); i++) nums.push(i);
    if (page < pages - 2) nums.push('…');
    nums.push(pages);
  }
  const box = 'px-3 py-1.5 rounded-md text-sm border border-border bg-card hover:bg-elevated';
  return (
    <div className="flex items-center justify-center gap-1 pt-1">
      <button className={`${box} disabled:opacity-40`} disabled={page <= 1} onClick={() => onPage(page - 1)}>Previous</button>
      {nums.map((n, i) =>
        n === '…' ? <span key={i} className="px-2 text-textSecondary">…</span>
        : <button key={i} className={n === page ? 'px-3 py-1.5 rounded-md text-sm bg-primary text-white border border-primary' : box} onClick={() => onPage(n)}>{n}</button>
      )}
      <button className={`${box} disabled:opacity-40`} disabled={page >= pages} onClick={() => onPage(page + 1)}>Next</button>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Anti-Hacking — 3CX shape with three sections (SIP / Live Chat / Global IP).
// Settings names + defaults match the live 3CX form.
// ────────────────────────────────────────────────────────────────────────────
export function AntiHacking() {
  const qc = useQueryClient();
  const { data: cfg } = useQuery({ queryKey: ['anti-hacking'], queryFn: () => api.advanced.antiHackingGet() });
  const [form, setForm] = useState<any>({});
  const f = { ...(cfg || {}), ...form };
  const set = (k: string, v: any) => setForm({ ...form, [k]: v });
  const save = useMutation({
    mutationFn: () => api.advanced.antiHackingSet(f),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['anti-hacking'] }),
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold flex-1">Anti-Hacking</h1>
        <button className="btn-primary text-sm inline-flex items-center gap-1" onClick={() => save.mutate()}>
          <Save size={14} /> Save
        </button>
      </div>

      {/* SIP Anti-Hacking Protection */}
      <div className="card p-4 space-y-4">
        <h2 className="font-semibold">SIP Anti-Hacking Protection</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Failed Authentication Protection *">
            <input className="input" type="number" min={1}
                   value={f.failed_auth_threshold ?? 3}
                   onChange={e => set('failed_auth_threshold', parseInt(e.target.value, 10) || 0)} />
            <div className="text-xs text-textSecondary mt-1">
              Configure the amount of failed authentications that Stratus Phone System will accept. If this value is exceeded the source IP address is put in the Blacklist
            </div>
          </Field>
          <Field label="Failed Challenge Requests (407) *">
            <input className="input" type="number" min={1}
                   value={f.failed_challenge_threshold ?? 50}
                   onChange={e => set('failed_challenge_threshold', parseInt(e.target.value, 10) || 0)} />
            <div className="text-xs text-textSecondary mt-1">
              DOS attacks can send REGISTER requests but do not reply to Challenge (407). Configure the amount of "fake" requests that Stratus will accept per IP Address before blacklisting the IP
            </div>
          </Field>
          <Field label="Blacklist time interval *">
            <input className="input" type="number" min={1}
                   value={f.blacklist_duration_seconds ?? 86400}
                   onChange={e => set('blacklist_duration_seconds', parseInt(e.target.value, 10) || 0)} />
            <div className="text-xs text-textSecondary mt-1">
              This is the time interval in seconds that an abusive IP Address remains in the blacklist
            </div>
          </Field>
          <Field label="Security Barrier (Green) *">
            <input className="input" type="number" min={1}
                   value={f.security_barrier_green_ms ?? 500}
                   onChange={e => set('security_barrier_green_ms', parseInt(e.target.value, 10) || 0)} />
            <div className="text-xs text-textSecondary mt-1">
              This is a time interval in milliseconds where counting starts but no action is enforced. If you want to disable security, set this to a high value.
            </div>
          </Field>
          <Field label="Security Barrier (Amber) *">
            <input className="input" type="number" min={1}
                   value={f.security_barrier_amber_ms ?? 2000}
                   onChange={e => set('security_barrier_amber_ms', parseInt(e.target.value, 10) || 0)} />
            <div className="text-xs text-textSecondary mt-1">
              If the amount of packets is exceeded, the PBX will block the source IP for 5 seconds
            </div>
          </Field>
          <Field label="Security Barrier (Red) *">
            <input className="input" type="number" min={1}
                   value={f.security_barrier_red_ms ?? 3000}
                   onChange={e => set('security_barrier_red_ms', parseInt(e.target.value, 10) || 0)} />
            <div className="text-xs text-textSecondary mt-1">
              This is the final protection layer. If the amount of packets specified below is exceeded, the PBX will blacklist the IP address. The IP will remain blacklisted until "Blacklist time interval" expires.
            </div>
          </Field>
        </div>
      </div>

      {/* Live Chat Requests Protection */}
      <div className="card p-4 space-y-4">
        <h2 className="font-semibold">Live Chat Requests Protection</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Maximum allowed requests per IP Address *">
            <input className="input" type="number" min={1}
                   value={f.live_chat_max_requests ?? 10}
                   onChange={e => set('live_chat_max_requests', parseInt(e.target.value, 10) || 0)} />
            <div className="text-xs text-textSecondary mt-1">Offending IPs will be blacklisted</div>
          </Field>
          <Field label="Request Rate Duration *">
            <input className="input" type="number" min={1}
                   value={f.live_chat_rate_window_seconds ?? 2}
                   onChange={e => set('live_chat_rate_window_seconds', parseInt(e.target.value, 10) || 0)} />
            <div className="text-xs text-textSecondary mt-1">
              Configure the duration in seconds in which the above requests are counted
            </div>
          </Field>
        </div>
      </div>

      {/* Automatic Global IP Blacklist */}
      <div className="card p-4 space-y-2">
        <h2 className="font-semibold">Automatic Global IP Blacklist</h2>
        <label className="inline-flex items-start gap-2 text-sm">
          <input type="checkbox" className="mt-0.5"
                 checked={!!f.contribute_to_global_blacklist}
                 onChange={e => set('contribute_to_global_blacklist', e.target.checked)} />
          <span>
            Allow Stratus to collect any IP's that are blacklisted from this instance, and in exchange for this contribution,
            the global Stratus Anti Hacking Defense Program list of blacklisted IP's will be downloaded and enabled on this installation.
          </span>
        </label>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Console Restrictions — 3CX shape: radio toggle (everywhere vs specific IPs)
// + current-IP banner + IP/Mask list with delete buttons.
// ────────────────────────────────────────────────────────────────────────────
const PRIVATE_RANGES = [
  '10.0.0.0/8',
  '172.16.0.0/12',
  '192.168.0.0/16',
  '169.254.0.0/16',
  'fe80::/10',
];

export function ConsoleRestrictions() {
  const qc = useQueryClient();
  const list = asArray(useQuery<any>({
    queryKey: ['console-restrictions'],
    queryFn: () => api.advanced.consoleList(),
  }).data);

  // Mode is local (the everywhere/specific persistence isn't wired yet).
  const [mode,    setMode]    = useState<'everywhere' | 'specific'>('everywhere');
  const [adding,  setAdding]  = useState(false);
  const [newCidr, setNewCidr] = useState('');

  // Auto-flip to "specific" when there are stored rules — runs once on first
  // load. We watch list.length (stable primitive) instead of list (new array
  // on every render).
  const haveRules = list.length > 0;
  useEffect(() => {
    if (haveRules) setMode('specific');
  }, [haveRules]);

  const add = useMutation({
    mutationFn: (cidr: string) => api.advanced.consoleAdd({ cidr, action: 'allow', description: '' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['console-restrictions'] }); setNewCidr(''); setAdding(false); },
  });
  const rm = useMutation({
    mutationFn: (id: string) => api.advanced.consoleRemove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['console-restrictions'] }),
  });

  // Private/link-local ranges are pre-populated and undeletable per 3CX behaviour.
  const merged = [
    ...list.map(r => ({ ...r, locked: false })),
    ...PRIVATE_RANGES
      .filter(p => !list.some((s: any) => s.cidr === p))
      .map(p => ({ rule_uuid: `locked:${p}`, cidr: p, action: 'allow', description: '', locked: true })),
  ];

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold flex-1">Console Restrictions</h1>
        <button className="btn-primary text-sm inline-flex items-center gap-1" onClick={() => { /* mode persistence wired in /api/advanced/console-restrictions config endpoint when ready */ }}>
          <Save size={14} /> Save
        </button>
      </div>

      <div className="card p-4 space-y-3">
        <p className="text-sm">Restrict the IP Addresses from which the Admin Console can be accessed</p>
        <div className="flex flex-col gap-2 text-sm">
          <label className="inline-flex items-center gap-2">
            <input type="radio" name="cr-mode" checked={mode === 'everywhere'}
                   onChange={() => setMode('everywhere')} />
            Allow access from everywhere
          </label>
          <label className="inline-flex items-center gap-2">
            <input type="radio" name="cr-mode" checked={mode === 'specific'}
                   onChange={() => setMode('specific')} />
            Allow access from specific IP Addresses
          </label>
        </div>


        {mode === 'specific' && (
          <>
            <p className="text-xs text-textSecondary">
              Click 'Add' below to enter permitted IP Addresses. IP Addresses added here are automatically included into the IP Blacklist and set to 'Allowed'.
              The Link-local IPv4, Link-local IPv6, and Private IPv4 Addresses are pre-populated already and cannot be deleted
            </p>

            {!adding ? (
              <button className="btn-primary text-sm inline-flex items-center gap-1" onClick={() => setAdding(true)}>
                <Plus size={14} /> Add
              </button>
            ) : (
              <div className="flex gap-1">
                <input className="input flex-1 font-mono" value={newCidr} placeholder="192.168.0.0/16"
                       onChange={e => setNewCidr(e.target.value)} />
                <button className="btn-primary text-sm" disabled={!newCidr} onClick={() => add.mutate(newCidr)}>Add</button>
                <button className="btn-ghost text-sm" onClick={() => { setAdding(false); setNewCidr(''); }}>Cancel</button>
              </div>
            )}

            <div className="space-y-1">
              <div className="text-sm font-medium">IP/Mask</div>
              {merged.map(r => (
                <div key={r.rule_uuid} className="flex items-center bg-elevated rounded-md px-3 py-2 text-sm">
                  <span className="font-mono flex-1">{r.cidr}</span>
                  {!r.locked && (
                    <button className="text-textSecondary hover:text-danger"
                            onClick={() => rm.mutate(r.rule_uuid)} title="Remove">
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function StunRow({ label, host, port, onHost, onPort }: {
  label: string; host: string; port: number;
  onHost: (v: string) => void; onPort: (v: number) => void;
}) {
  return (
    <div>
      <label className="block text-sm text-textSecondary mb-1">{label}</label>
      <div className="grid grid-cols-[1fr_120px] gap-2">
        <input className="input" value={host} onChange={e => onHost(e.target.value)} />
        <input className="input" type="number" min={1} max={65535} value={port}
               onChange={e => onPort(parseInt(e.target.value, 10) || 0)} />
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Network — 3CX shape: NIC selector + IPv6 toggle, Static/Dynamic IP radios,
// 3 STUN server slots + query interval. Stratus extras (RTP port range,
// SBC tunnel port, keep-alive) get tucked at the bottom — those don't exist
// in 3CX but the API still consumes them.
// ────────────────────────────────────────────────────────────────────────────
export function NetworkSettings() {
  const qc = useQueryClient();
  const rawCfg = useQuery<any>({ queryKey: ['network'], queryFn: () => api.advanced.networkGet() }).data;
  const cfg: any = rawCfg && typeof rawCfg === 'object' && !Array.isArray(rawCfg) ? rawCfg : {};

  // availableNics is an array of {name, ip} objects (eth0/ip etc). Tolerate
  // legacy shape (just strings) too.
  const nicList: { name: string; ip: string }[] =
    Array.isArray(cfg.availableNics)
      ? cfg.availableNics.map((n: any) => typeof n === 'string' ? { name: n, ip: n } : n)
      : [];

  const [form, setForm] = useState<any>({});
  const f = {
    nic:            cfg.detectedPublicIp || '',
    staticPublicIp: cfg.detectedPublicIp || '',
    publicIpType:   'static',
    stunHost:       'stun.l.google.com',
    stunPort:       19302,
    ...cfg,
    ...form,
  };
  const set = (k: string, v: any) => setForm({ ...form, [k]: v });

  const save = useMutation({
    mutationFn: () => api.advanced.networkSet(f),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['network'] }),
  });

  const ipMode: 'static' | 'dynamic' = f.publicIpType === 'dynamic' ? 'dynamic' : 'static';

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold flex-1">Network</h1>
        <button className="btn-primary text-sm inline-flex items-center gap-1" onClick={() => save.mutate()}>
          <Save size={14} /> Save
        </button>
      </div>

      {/* Select network card interface */}
      <div className="card p-4 space-y-3">
        <h2 className="font-semibold">Select network card interface</h2>
        <Field label="Choose which IP is the Default Internet facing IP address (Default gateway)">
          {nicList.length > 0 ? (
            <select className="input" value={f.nic || ''}
                    onChange={e => set('nic', e.target.value)}>
              <option value="">—</option>
              {/* Detected public IP first if not in NIC list */}
              {cfg.detectedPublicIp && !nicList.some(n => n.ip === cfg.detectedPublicIp) && (
                <option key="detected" value={cfg.detectedPublicIp}>{cfg.detectedPublicIp} (public)</option>
              )}
              {nicList.map(n => (
                <option key={`${n.name}-${n.ip}`} value={n.ip}>
                  {n.ip}{n.name ? ` (${n.name})` : ''}
                </option>
              ))}
            </select>
          ) : (
            <input className="input font-mono" value={f.nic || ''}
                   onChange={e => set('nic', e.target.value)} />
          )}
        </Field>
        <label className="inline-flex items-start gap-2 text-sm">
          <input type="checkbox" className="mt-0.5"
                 checked={!!f.ipv6}
                 onChange={e => set('ipv6', e.target.checked)} />
          <span>
            Automatically bind to IPv6 adapters. Requires SIP service restart
            <span className="block text-xs text-textSecondary">
              (Go to Dashboard, Services, select 'Stratus Phone System 01 SIP Server' and restart)
            </span>
          </span>
        </label>
      </div>

      {/* External IP configuration */}
      <div className="card p-4 space-y-3">
        <h2 className="font-semibold">External IP configuration</h2>
        <div className="space-y-2">
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="radio" name="net-ip-mode" checked={ipMode === 'static'}
                   onChange={() => set('publicIpType', 'static')} />
            Static public IP (IP Address does not change)
          </label>
          {ipMode === 'static' && (
            <Field label="Enter your Static Public IP">
              <input className="input font-mono" value={f.staticPublicIp || ''}
                     onChange={e => set('staticPublicIp', e.target.value)} />
            </Field>
          )}
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="radio" name="net-ip-mode" checked={ipMode === 'dynamic'}
                   onChange={() => set('publicIpType', 'dynamic')} />
            Dynamic public IP (IP changes)
          </label>
        </div>

        <StunRow label="Primary STUN server"
                 host={f.stunHost ?? 'stun.l.google.com'}
                 port={f.stunPort ?? 19302}
                 onHost={(v: string) => set('stunHost', v)}
                 onPort={(v: number) => set('stunPort', v)} />
        <StunRow label="Secondary STUN server"
                 host={f.stunHost2 ?? ''}
                 port={f.stunPort2 ?? 3478}
                 onHost={(v: string) => set('stunHost2', v)}
                 onPort={(v: number) => set('stunPort2', v)} />
        <StunRow label="Third STUN server"
                 host={f.stunHost3 ?? ''}
                 port={f.stunPort3 ?? 3478}
                 onHost={(v: string) => set('stunHost3', v)}
                 onPort={(v: number) => set('stunPort3', v)} />

        <Field label="Query STUN server every (sec)">
          <input className="input" type="number" min={60} value={f.stunQueryInterval ?? 1200}
                 onChange={e => set('stunQueryInterval', parseInt(e.target.value, 10) || 0)} />
        </Field>
      </div>

      {/* Stratus extras — kept in a single subtle card so it's clear these
          aren't part of the 3CX form. Drives FreeSWITCH RTP/keep-alive bits. */}
      <div className="card p-4 space-y-3">
        <h2 className="font-semibold">Stratus advanced</h2>
        <div className="grid grid-cols-2 gap-3">
          <Field label="SIP External Port">
            <input className="input" type="number" value={f.sip_external_port || 5060}
                   onChange={e => set('sip_external_port', parseInt(e.target.value, 10) || 0)} />
          </Field>
          <Field label="SBC Tunnel Port">
            <input className="input" type="number" value={f.sbc_tunnel_port || 5090}
                   onChange={e => set('sbc_tunnel_port', parseInt(e.target.value, 10) || 0)} />
          </Field>
          <Field label="RTP Port Min">
            <input className="input" type="number" value={f.rtp_port_min || 16384}
                   onChange={e => set('rtp_port_min', parseInt(e.target.value, 10) || 0)} />
          </Field>
          <Field label="RTP Port Max">
            <input className="input" type="number" value={f.rtp_port_max || 32768}
                   onChange={e => set('rtp_port_max', parseInt(e.target.value, 10) || 0)} />
          </Field>
          <Field label="Keep-alive (seconds)">
            <input className="input" type="number" value={f.keep_alive_interval || 30}
                   onChange={e => set('keep_alive_interval', parseInt(e.target.value, 10) || 0)} />
          </Field>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Allowed Country Codes — 3CX shape: 8-region accordion with per-region
// "all" toggle + per-country checkboxes inside. Stored as a flat map of
// ISO 3166-1 alpha-2 → boolean.
// ────────────────────────────────────────────────────────────────────────────

// Country = E.164 dialing-prefix entry. `code` is the dial prefix (numeric
// string), `name` is the country/territory name. Some entries share a prefix
// (US/CA both 1; Russia/Kazakhstan both 7) and some countries have multiple
// prefixes (Dominican Republic 1809/1829/1849, Jamaica 1876/1658).
type Country = { code: string; name: string };
type Region  = { name: string; countries: Country[] };

// Full 3CX-shape dataset — copied directly from the 3CX Allowed Country Codes
// page. Uses dialing-prefix keys so outbound-rule matching (which is prefix-
// based) lines up with the on/off toggle.
const REGIONS: Region[] = [
  { name: 'North America', countries: [
    { name: 'United States',                        code: '1' },
    { name: 'Canada',                               code: '1' },
    { name: 'Anguilla',                             code: '1264' },
    { name: 'Antigua and Barbuda',                  code: '1268' },
    { name: 'Bahamas',                              code: '1242' },
    { name: 'Barbados',                             code: '1246' },
    { name: 'Bermuda',                              code: '1441' },
    { name: 'Cayman Islands',                       code: '1345' },
    { name: 'Costa Rica',                           code: '506' },
    { name: 'Cuba',                                 code: '53' },
    { name: 'Dominica',                             code: '1767' },
    { name: 'Dominican Republic',                   code: '1809' },
    { name: 'Dominican Republic',                   code: '1829' },
    { name: 'Dominican Republic',                   code: '1849' },
    { name: 'El Salvador',                          code: '503' },
    { name: 'Greenland',                            code: '299' },
    { name: 'Grenada',                              code: '1473' },
    { name: 'Guam',                                 code: '1671' },
    { name: 'Guatemala',                            code: '502' },
    { name: 'Haiti',                                code: '509' },
    { name: 'Honduras',                             code: '504' },
    { name: 'Jamaica',                              code: '1876' },
    { name: 'Jamaica',                              code: '1658' },
    { name: 'Mexico',                               code: '52' },
    { name: 'Montserrat',                           code: '1664' },
    { name: 'Nicaragua',                            code: '505' },
    { name: 'Northern Mariana Islands',             code: '1670' },
    { name: 'Panama',                               code: '507' },
    { name: 'Puerto Rico',                          code: '1787' },
    { name: 'Puerto Rico',                          code: '1939' },
    { name: 'St. Kitts and Nevis',                  code: '1869' },
    { name: 'St. Lucia',                            code: '1758' },
    { name: 'St. Vincent and the Grenadines',       code: '1784' },
    { name: 'Trinidad and Tobago',                  code: '1868' },
    { name: 'Turks and Caicos Islands',             code: '1649' },
    { name: 'Virgin Islands, British',              code: '1284' },
    { name: 'Virgin Islands, U.S.',                 code: '1340' },
    { name: 'Sint Maarten',                         code: '1721' },
  ]},
  { name: 'South America', countries: [
    { name: 'Argentina',                            code: '54' },
    { name: 'Aruba',                                code: '297' },
    { name: 'Belize',                               code: '501' },
    { name: 'Bolivia',                              code: '591' },
    { name: 'Brazil',                               code: '55' },
    { name: 'Chile',                                code: '56' },
    { name: 'Colombia',                             code: '57' },
    { name: 'Ecuador',                              code: '593' },
    { name: 'Falkland Islands',                     code: '500' },
    { name: 'French Guiana',                        code: '594' },
    { name: 'Guadeloupe',                           code: '590' },
    { name: 'Guyana',                               code: '592' },
    { name: 'Martinique',                           code: '596' },
    { name: 'Paraguay',                             code: '595' },
    { name: 'Peru',                                 code: '51' },
    { name: 'St. Pierre and Miquelon',              code: '508' },
    { name: 'Suriname',                             code: '597' },
    { name: 'Uruguay',                              code: '598' },
    { name: 'Venezuela',                            code: '58' },
    { name: 'Curacao and Caribbean Netherlands',    code: '5993' },
    { name: 'Curacao and Caribbean Netherlands',    code: '5994' },
    { name: 'Curacao and Caribbean Netherlands',    code: '5995' },
    { name: 'Curacao and Caribbean Netherlands',    code: '5996' },
    { name: 'Curacao and Caribbean Netherlands',    code: '5997' },
    { name: 'Curacao and Caribbean Netherlands',    code: '5998' },
    { name: 'Curacao and Caribbean Netherlands',    code: '5999' },
  ]},
  { name: 'Europe', countries: [
    { name: 'Albania',                              code: '355' },
    { name: 'Andorra',                              code: '376' },
    { name: 'Armenia',                              code: '374' },
    { name: 'Austria',                              code: '43' },
    { name: 'Belarus',                              code: '375' },
    { name: 'Belgium',                              code: '32' },
    { name: 'Bosnia and Herzegovina',               code: '387' },
    { name: 'Bulgaria',                             code: '359' },
    { name: 'Croatia',                              code: '385' },
    { name: 'Cyprus',                               code: '357' },
    { name: 'Czech Republic',                       code: '420' },
    { name: 'Denmark',                              code: '45' },
    { name: 'Estonia',                              code: '372' },
    { name: 'Faroe Islands',                        code: '298' },
    { name: 'Finland',                              code: '358' },
    { name: 'France',                               code: '33' },
    { name: 'Germany',                              code: '49' },
    { name: 'Gibraltar',                            code: '350' },
    { name: 'Greece',                               code: '30' },
    { name: 'Guernsey',                             code: '441481' },
    { name: 'Hungary',                              code: '36' },
    { name: 'Iceland',                              code: '354' },
    { name: 'Ireland',                              code: '353' },
    { name: 'Isle of Man',                          code: '441624' },
    { name: 'Italy',                                code: '39' },
    { name: 'Jersey',                               code: '441534' },
    { name: 'Latvia',                               code: '371' },
    { name: 'Liechtenstein',                        code: '423' },
    { name: 'Lithuania',                            code: '370' },
    { name: 'Luxembourg',                           code: '352' },
    { name: 'North Macedonia',                      code: '389' },
    { name: 'Malta',                                code: '356' },
    { name: 'Moldova',                              code: '373' },
    { name: 'Monaco',                               code: '377' },
    { name: 'Montenegro',                           code: '382' },
    { name: 'Netherlands',                          code: '31' },
    { name: 'Norway',                               code: '47' },
    { name: 'Poland',                               code: '48' },
    { name: 'Portugal',                             code: '351' },
    { name: 'Romania',                              code: '40' },
    { name: 'San Marino',                           code: '378' },
    { name: 'Serbia',                               code: '381' },
    { name: 'Kosovo',                               code: '383' },
    { name: 'Slovakia',                             code: '421' },
    { name: 'Slovenia',                             code: '386' },
    { name: 'Spain',                                code: '34' },
    { name: 'Svalbard and Jan Mayen',               code: '4779' },
    { name: 'Sweden',                               code: '46' },
    { name: 'Switzerland',                          code: '41' },
    { name: 'Turkey',                               code: '90' },
    { name: 'Turkish Republic of Northern Cyprus',  code: '90392' },
    { name: 'Ukraine',                              code: '380' },
    { name: 'United Kingdom',                       code: '44' },
    { name: 'Vatican City State',                   code: '3906698' },
  ]},
  { name: 'Middle East', countries: [
    { name: 'United Arab Emirates',                 code: '971' },
  ]},
  { name: 'Asia and the Middle East', countries: [
    { name: 'Afghanistan',                          code: '93' },
    { name: 'Azerbaijan',                           code: '994' },
    { name: 'Bahrain',                              code: '973' },
    { name: 'Bangladesh',                           code: '880' },
    { name: 'Bhutan',                               code: '975' },
    { name: 'Brunei Darussalam',                    code: '673' },
    { name: 'Cambodia',                             code: '855' },
    { name: 'China',                                code: '86' },
    { name: 'East Timor',                           code: '670' },
    { name: 'Georgia',                              code: '995' },
    { name: 'Hong Kong',                            code: '852' },
    { name: 'India',                                code: '91' },
    { name: 'Indonesia',                            code: '62' },
    { name: 'Iran',                                 code: '98' },
    { name: 'Iraq',                                 code: '964' },
    { name: 'Israel',                               code: '972' },
    { name: 'Japan',                                code: '81' },
    { name: 'Jordan',                               code: '962' },
    { name: 'Kuwait',                               code: '965' },
    { name: 'Kyrgyz Republic',                      code: '996' },
    { name: 'Laos',                                 code: '856' },
    { name: 'Lebanon',                              code: '961' },
    { name: 'Macao',                                code: '853' },
    { name: 'Malaysia',                             code: '60' },
    { name: 'Maldives',                             code: '960' },
    { name: 'Mongolia',                             code: '976' },
    { name: 'Myanmar',                              code: '95' },
    { name: 'Nepal',                                code: '977' },
    { name: 'North Korea',                          code: '850' },
    { name: 'Oman',                                 code: '968' },
    { name: 'Pakistan',                             code: '92' },
    { name: 'Palestine',                            code: '970' },
    { name: 'Philippines',                          code: '63' },
    { name: 'Qatar',                                code: '974' },
    { name: 'Russia',                               code: '7' },
    { name: 'Saudi Arabia',                         code: '966' },
    { name: 'Singapore',                            code: '65' },
    { name: 'South Korea',                          code: '82' },
    { name: 'Sri Lanka',                            code: '94' },
    { name: 'Syria',                                code: '963' },
    { name: 'Taiwan',                               code: '886' },
    { name: 'Tajikistan',                           code: '992' },
    { name: 'Thailand',                             code: '66' },
    { name: 'Turkmenistan',                         code: '993' },
    { name: 'Uzbekistan',                           code: '998' },
    { name: 'Kazakhstan',                           code: '7' },
    { name: 'Vietnam',                              code: '84' },
    { name: 'Yemen',                                code: '967' },
  ]},
  { name: 'Africa', countries: [
    { name: 'Algeria',                              code: '213' },
    { name: 'Angola',                               code: '244' },
    { name: 'Ascension Island',                     code: '247' },
    { name: 'Benin',                                code: '229' },
    { name: 'Botswana',                             code: '267' },
    { name: 'British Indian Ocean Territory',       code: '246' },
    { name: 'Burkina Faso',                         code: '226' },
    { name: 'Burundi',                              code: '257' },
    { name: 'Cameroon',                             code: '237' },
    { name: 'Cape Verde',                           code: '238' },
    { name: 'Central African Republic',             code: '236' },
    { name: 'Chad',                                 code: '235' },
    { name: 'Comoros',                              code: '269' },
    { name: 'Congo',                                code: '242' },
    { name: 'Congo (DRC)',                          code: '243' },
    { name: "Cote d'Ivoire",                        code: '225' },
    { name: 'Djibouti',                             code: '253' },
    { name: 'Egypt',                                code: '20' },
    { name: 'Equatorial Guinea',                    code: '240' },
    { name: 'Eritrea',                              code: '291' },
    { name: 'Ethiopia',                             code: '251' },
    { name: 'Gabon',                                code: '241' },
    { name: 'Gambia',                               code: '220' },
    { name: 'Ghana',                                code: '233' },
    { name: 'Guinea',                               code: '224' },
    { name: 'Guinea-Bissau',                        code: '245' },
    { name: 'Kenya',                                code: '254' },
    { name: 'Lesotho',                              code: '266' },
    { name: 'Liberia',                              code: '231' },
    { name: 'Libya',                                code: '218' },
    { name: 'Madagascar',                           code: '261' },
    { name: 'Malawi',                               code: '265' },
    { name: 'Mali',                                 code: '223' },
    { name: 'Mauritania',                           code: '222' },
    { name: 'Mauritius',                            code: '230' },
    { name: 'Mayotte',                              code: '262269' },
    { name: 'Mayotte',                              code: '269639' },
    { name: 'Morocco',                              code: '212' },
    { name: 'Mozambique',                           code: '258' },
    { name: 'Namibia',                              code: '264' },
    { name: 'Niger',                                code: '227' },
    { name: 'Nigeria',                              code: '234' },
    { name: 'Reunion',                              code: '262' },
    { name: 'Rwanda',                               code: '250' },
    { name: 'St. Helena',                           code: '290' },
    { name: 'Sao Tome and Principe',                code: '239' },
    { name: 'Senegal',                              code: '221' },
    { name: 'Seychelles',                           code: '248' },
    { name: 'Sierra Leone',                         code: '232' },
    { name: 'Somalia',                              code: '252' },
    { name: 'South Africa',                         code: '27' },
    { name: 'South Sudan',                          code: '211' },
    { name: 'Sudan',                                code: '249' },
    { name: 'Eswatini',                             code: '268' },
    { name: 'Tanzania',                             code: '255' },
    { name: 'Togo',                                 code: '228' },
    { name: 'Tristan da Cunha',                     code: '2908' },
    { name: 'Tunisia',                              code: '216' },
    { name: 'Uganda',                               code: '256' },
    { name: 'Zambia',                               code: '260' },
    { name: 'Zanzibar',                             code: '25524' },
    { name: 'Zimbabwe',                             code: '263' },
  ]},
  { name: 'Australia', countries: [
    { name: 'American Samoa',                       code: '1684' },
    { name: 'Australia',                            code: '61' },
    { name: 'Christmas Island',                     code: '61' },
    { name: 'Cocos (Keeling) Island',               code: '6189162' },
    { name: 'Cook Islands',                         code: '682' },
    { name: 'Fiji',                                 code: '679' },
    { name: 'French Polynesia',                     code: '689' },
    { name: 'Kiribati',                             code: '686' },
    { name: 'Marshall Islands',                     code: '692' },
    { name: 'Micronesia',                           code: '691' },
    { name: 'Nauru',                                code: '674' },
    { name: 'New Caledonia',                        code: '687' },
    { name: 'New Zealand',                          code: '64' },
    { name: 'Niue',                                 code: '683' },
    { name: 'Norfolk Island',                       code: '672' },
    { name: 'Palau',                                code: '680' },
    { name: 'Papua New Guinea',                     code: '675' },
    { name: 'Pitcairn',                             code: '872' },
    { name: 'Samoa',                                code: '685' },
    { name: 'Solomon Islands',                      code: '677' },
    { name: 'Tokelau',                              code: '690' },
    { name: 'Tonga',                                code: '676' },
    { name: 'Tuvalu',                               code: '688' },
    { name: 'United States Minor Outlying Islands', code: '699' },
    { name: 'Vanuatu',                              code: '678' },
    { name: 'Wallis and Futuna Islands',            code: '681' },
  ]},
  { name: 'International Networks', countries: [
    { name: 'Inmarsat SNAC Service',                                                    code: '870' },
    { name: 'Unassigned (formerly used by Inmarsat, Atlantic East), discontinued in 2008', code: '871' },
    { name: 'Unassigned (formerly used by Inmarsat, Indian), discontinued in 2008',     code: '873' },
    { name: 'Unassigned (formerly used by Inmarsat, Atlantic West), discontinued 2008', code: '874' },
    { name: 'Reserved for Maritime Mobile service',                                     code: '875' },
    { name: 'Reserved for Maritime Mobile service',                                     code: '876' },
    { name: 'Reserved for Maritime Mobile service',                                     code: '877' },
    { name: 'Universal Personal Telecommunications services',                           code: '878' },
    { name: 'Reserved for national non-commercial purposes',                            code: '879' },
    { name: 'Global Mobile Satellite System',                                           code: '881' },
    { name: 'International Networks',                                                   code: '882' },
    { name: 'International Networks',                                                   code: '883' },
    { name: 'International Premium Rate Service',                                       code: '979' },
    { name: 'Universal International Freephone Number',                                 code: '800' },
    { name: 'Universal International Shared Cost Number',                               code: '808' },
    { name: 'United Nations Office for the Coordination of Humanitarian Affairs',       code: '888' },
    { name: 'International Telecommunications Public Correspondence Service trial',     code: '991' },
    { name: 'Reserved for future global service',                                       code: '999' },
  ]},
];

// Many entries share a dial prefix (US/CA both 1; Russia/Kazakhstan both 7).
// Per-row uniqueness is `${code}|${name}` so the toggle map can address each
// country independently even when prefixes collide.
function rowKey(c: Country) { return `${c.code}|${c.name}`; }

export function AllowedCountries() {
  const qc = useQueryClient();
  const raw = useQuery<any>({
    queryKey: ['country-codes'],
    queryFn: () => api.advanced.countryList(),
  }).data;

  // API returns either { countries: {US:true,...} } or the flat map directly.
  const saved: Record<string, boolean> =
    raw && typeof raw === 'object' && !Array.isArray(raw)
      ? (raw.countries || raw)
      : {};

  const [allowed, setAllowed] = useState<Record<string, boolean>>(saved);
  useEffect(() => { setAllowed(saved); }, [JSON.stringify(saved)]);

  const [openRegion, setOpenRegion] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => api.advanced.countrySet
      ? api.advanced.countrySet({ countries: allowed })
      : fetch('/api/advanced/country-codes', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
          body: JSON.stringify({ countries: allowed }),
        }).then(r => r.json()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['country-codes'] }),
  });

  function regionState(region: Region): 'all' | 'some' | 'none' {
    const ons = region.countries.filter(c => allowed[rowKey(c)]).length;
    if (ons === 0) return 'none';
    if (ons === region.countries.length) return 'all';
    return 'some';
  }
  function toggleRegion(region: Region) {
    const turnOn = regionState(region) !== 'all';
    const next = { ...allowed };
    for (const c of region.countries) next[rowKey(c)] = turnOn;
    setAllowed(next);
  }
  function toggleCountry(c: Country) {
    const k = rowKey(c);
    setAllowed({ ...allowed, [k]: !allowed[k] });
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold flex-1">Allowed Country Codes</h1>
        <button className="btn-primary text-sm inline-flex items-center gap-1" onClick={() => save.mutate()}>
          <Save size={14} /> Save
        </button>
      </div>

      <div className="card p-0 overflow-hidden divide-y divide-border">
        {REGIONS.map(region => {
          const state = regionState(region);
          const open = openRegion === region.name;
          return (
            <div key={region.name}>
              <div className="flex items-center px-4 py-3 hover:bg-elevated cursor-pointer"
                   onClick={() => setOpenRegion(open ? null : region.name)}>
                <input
                  type="checkbox"
                  className="mr-3"
                  checked={state === 'all'}
                  ref={el => { if (el) el.indeterminate = state === 'some'; }}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => toggleRegion(region)}
                />
                <div className="flex-1 font-medium">{region.name}</div>
                <div className="text-xs text-textSecondary mr-3">
                  {state === 'all'  && 'All allowed'}
                  {state === 'some' && `${region.countries.filter(c => allowed[rowKey(c)]).length} of ${region.countries.length}`}
                  {state === 'none' && 'None'}
                </div>
                <span className={`transform transition-transform ${open ? 'rotate-180' : ''}`}>▾</span>
              </div>
              {open && (
                <div className="px-4 py-3 bg-elevated/50 grid grid-cols-2 md:grid-cols-3 gap-1">
                  {region.countries.map(c => {
                    const k = rowKey(c);
                    return (
                      <label key={k} className="inline-flex items-center gap-2 text-sm py-0.5">
                        <input
                          type="checkbox"
                          checked={!!allowed[k]}
                          onChange={() => toggleCountry(c)}
                        />
                        <span>{c.name}<span className="text-textSecondary">({c.code})</span></span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// CID Rules
// ────────────────────────────────────────────────────────────────────────────
// ────────────────────────────────────────────────────────────────────────────
// CID Rules — 3CX shape: per-caller routing rules. If a call matches the
// Caller ID on the named trunk, route it to the picked destination instead
// of the trunk's normal inbound rule.
// ────────────────────────────────────────────────────────────────────────────

type CidRow = {
  id?: string;
  name: string;
  trunk: string;          // trunk uuid or display name
  caller_id: string;      // the caller-id to match against
  route_to: { kind: string; value: string; label?: string };
};

const ROUTE_KINDS = [
  { v: 'extension',    label: 'Extension' },
  { v: 'ring_group',   label: 'Ring Group' },
  { v: 'queue',        label: 'Queue' },
  { v: 'ivr',          label: 'Digital Receptionist' },
  { v: 'voicemail',    label: 'Voicemail' },
  { v: 'external',     label: 'External Number' },
  { v: 'end_call',     label: 'End Call' },
];

export function CIDRules() {
  const qc = useQueryClient();
  const list: CidRow[] = asArray(useQuery<any>({ queryKey: ['cid-rules'], queryFn: () => api.advanced.cidList() }).data);
  const trunks: any[]  = asArray(useQuery<any>({ queryKey: ['trunks'],    queryFn: () => fetch('/api/trunks', { headers: { Authorization: `Bearer ${getToken()}` } }).then(r => r.json()) }).data);

  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [editorRow, setEditorRow] = useState<CidRow | null | undefined>(undefined);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return list.filter(r =>
      !q || `${r.name} ${r.trunk} ${r.caller_id} ${r.route_to?.label || ''}`.toLowerCase().includes(q)
    );
  }, [list, search]);

  const remove = useMutation({
    mutationFn: (ids: string[]) => Promise.all(ids.map(id => api.advanced.cidRemove(id))),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['cid-rules'] }); setSelected(new Set()); },
  });

  function toggle(id: string) {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  }
  function toggleAll() {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map(r => r.id || '').filter(Boolean)));
  }

  // Export via server endpoint so the downloaded CSV matches the actual DB
  // schema (rule_name, direction, match_pattern, build_pattern, rule_order,
  // enabled, description) rather than the client-side view shape.
  async function exportCsv() {
    try {
      const resp = await fetch('/api/advanced/cid-rules/export.csv', {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!resp.ok) throw new Error(`Export failed: ${resp.status}`);
      const blob = await resp.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `cid-rules-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e: any) {
      alert(`Export error: ${e.message}`);
    }
  }

  // Import via server endpoint — single multipart POST instead of N serial
  // API calls, and upserts by rule_name so re-imports are safe.
  function importCsv() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv,text/csv';
    input.onchange = async () => {
      const f = input.files?.[0];
      if (!f) return;
      const form = new FormData();
      form.append('file', f);
      try {
        const resp = await fetch('/api/advanced/cid-rules/import-csv', {
          method: 'POST',
          headers: { Authorization: `Bearer ${getToken()}` },
          body: form,
        });
        const data = await resp.json();
        if (!resp.ok) throw new Error(data.error || String(resp.status));
        qc.invalidateQueries({ queryKey: ['cid-rules'] });
        const errNote = data.errors?.length ? `\nErrors: ${data.errors.join('; ')}` : '';
        alert(`Imported ${data.imported} rule(s). Skipped (empty name): ${data.skipped}.${errNote}`);
      } catch (e: any) {
        alert(`Import error: ${e.message}`);
      }
    };
    input.click();
  }

  return (
    <div className="space-y-3">
      <h1 className="text-xl font-semibold">CID Rules</h1>

      {/* Toolbar — 3CX shape */}
      <div className="card p-2 flex flex-wrap gap-1 items-center">
        <button className="btn-primary text-sm inline-flex items-center gap-1" onClick={() => setEditorRow(null)}>
          <Plus size={14} /> Add
        </button>
        <button className="btn-primary text-sm inline-flex items-center gap-1"
                onClick={() => {
                  if (selected.size !== 1) return alert('Select exactly one row to edit.');
                  setEditorRow(filtered.find(r => r.id === [...selected][0]) || null);
                }}>
          <Edit2 size={14} /> Edit
        </button>
        <button className="btn-primary text-sm inline-flex items-center gap-1"
                onClick={() => {
                  if (!selected.size) return alert('Select rows to delete.');
                  if (confirm(`Delete ${selected.size} rule(s)?`)) remove.mutate([...selected]);
                }}>
          <Trash2 size={14} /> Delete
        </button>
        <button className="btn-primary text-sm inline-flex items-center gap-1" onClick={importCsv}>
          <Upload size={14} /> Import
        </button>
        <button className="btn-primary text-sm inline-flex items-center gap-1" onClick={exportCsv}>
          <Download size={14} /> Export ({filtered.length})
        </button>
      </div>

      <div className="flex items-center gap-1 bg-elevated rounded-md px-2 py-1.5">
        <Search size={14} className="text-textSecondary" />
        <input className="bg-transparent outline-none text-sm w-full"
               value={search} onChange={e => setSearch(e.target.value)}
               placeholder="Search…" />
      </div>

      <div className="card p-0 overflow-x-auto">
        <table className="table">
          <thead>
            <tr>
              <th className="w-8">
                <input type="checkbox"
                       checked={filtered.length > 0 && selected.size === filtered.length}
                       onChange={toggleAll} />
              </th>
              <th>Name</th>
              <th>Trunk</th>
              <th>Caller ID</th>
              <th>Route to</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(r => (
              <tr key={r.id || `${r.name}-${r.caller_id}`}
                  className={`hover:bg-elevated cursor-pointer ${selected.has(r.id || '') ? 'bg-primary/5' : ''}`}
                  onClick={() => setEditorRow(r)}>
                <td onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" checked={selected.has(r.id || '')}
                         onChange={() => r.id && toggle(r.id)} />
                </td>
                <td className="text-sm">{r.name}</td>
                <td className="text-sm">{r.trunk}</td>
                <td className="font-mono text-sm">{r.caller_id}</td>
                <td className="text-sm">
                  {r.route_to
                    ? `${ROUTE_KINDS.find(k => k.v === r.route_to.kind)?.label || r.route_to.kind} (${r.route_to.label || r.route_to.value})`
                    : ''}
                </td>
              </tr>
            ))}
            {!filtered.length && (
              <tr><td colSpan={5} className="text-center py-8 text-textSecondary">No CID rules.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editorRow !== undefined && (
        <CidRuleEditor
          row={editorRow}
          trunks={trunks}
          onClose={() => setEditorRow(undefined)}
          onSaved={() => { qc.invalidateQueries({ queryKey: ['cid-rules'] }); setEditorRow(undefined); }}
        />
      )}
    </div>
  );
}

function CidRuleEditor({ row, trunks, onClose, onSaved }: {
  row: CidRow | null; trunks: any[];
  onClose: () => void; onSaved: () => void;
}) {
  const [name,       setName]       = useState(row?.name || '');
  const [trunk,      setTrunk]      = useState(row?.trunk || '');
  const [callerId,   setCallerId]   = useState(row?.caller_id || '');
  const [routeKind,  setRouteKind]  = useState(row?.route_to?.kind || 'extension');
  const [routeValue, setRouteValue] = useState(row?.route_to?.value || '');
  const [routeLabel, setRouteLabel] = useState(row?.route_to?.label || '');
  const [busy, setBusy] = useState(false);
  const [err,  setErr]  = useState('');

  async function go() {
    if (!name)     { setErr('Name required'); return; }
    if (!trunk)    { setErr('Trunk required'); return; }
    if (!callerId) { setErr('Caller ID required'); return; }
    setBusy(true); setErr('');
    try {
      const payload = {
        name, trunk, caller_id: callerId,
        route_to: { kind: routeKind, value: routeValue, label: routeLabel },
      };
      if (row?.id) await api.advanced.cidUpdate?.(row.id, payload) ?? api.advanced.cidAdd(payload);
      else          await api.advanced.cidAdd(payload);
      onSaved();
    } catch (e: any) { setErr(String(e.message || e)); }
    finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center" onClick={onClose}>
      <div className="card p-4 w-full max-w-lg" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-semibold mb-3">{row?.id ? 'Edit CID Rule' : 'Add CID Rule'}</h2>
        <div className="space-y-3">
          <Field label="Name *">
            <input className="input" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Block VIP customer" />
          </Field>
          <Field label="Trunk *">
            <select className="input" value={trunk} onChange={e => setTrunk(e.target.value)}>
              <option value="">— Choose —</option>
              {trunks.map((t: any) => (
                <option key={t.gateway_uuid || t.trunk_uuid || t.name} value={t.gateway || t.name || ''}>
                  {t.gateway || t.name || '(unnamed)'}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Caller ID *">
            <input className="input font-mono" value={callerId} onChange={e => setCallerId(e.target.value)} placeholder="+19893329523" />
          </Field>
          <div>
            <label className="block text-sm text-textSecondary mb-1">Route to</label>
            <div className="grid grid-cols-2 gap-2">
              <select className="input" value={routeKind} onChange={e => setRouteKind(e.target.value)}>
                {ROUTE_KINDS.map(k => <option key={k.v} value={k.v}>{k.label}</option>)}
              </select>
              {routeKind !== 'end_call' && (
                <input className="input" value={routeValue} onChange={e => setRouteValue(e.target.value)}
                       placeholder="extension / number / uuid" />
              )}
            </div>
            {routeKind !== 'end_call' && (
              <input className="input mt-2" value={routeLabel} onChange={e => setRouteLabel(e.target.value)}
                     placeholder="Optional display label (e.g. 'Digital Receptionist (8084 Infinite Call Hell)')" />
            )}
          </div>
        </div>
        {err && <div className="text-sm text-danger mt-2">{err}</div>}
        <div className="flex justify-end gap-2 mt-4">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={busy} onClick={go}>{busy ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// CID Blacklist — 3CX shape: simple two-column list (Blacklisted Caller ID +
// Description), Add / Edit / Delete / Import toolbar.
// ────────────────────────────────────────────────────────────────────────────

type CidBlRow = { id?: string; caller_id: string; description?: string };

export function CIDBlacklist() {
  const qc = useQueryClient();
  const list: CidBlRow[] = asArray(useQuery<any>({
    queryKey: ['cid-blacklist'],
    queryFn: () => fetch('/api/advanced/cid-blacklist', { headers: { Authorization: `Bearer ${getToken()}` } }).then(r => r.json()),
  }).data);

  const [search, setSearch]       = useState('');
  const [selected, setSelected]   = useState<Set<string>>(new Set());
  const [editorRow, setEditorRow] = useState<CidBlRow | null | undefined>(undefined);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return list.filter(r => !q || `${r.caller_id} ${r.description || ''}`.toLowerCase().includes(q));
  }, [list, search]);

  const post = (path: string, init: RequestInit) =>
    fetch(`/api/advanced/cid-blacklist${path}`, {
      ...init,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}`, ...(init.headers || {}) },
    }).then(r => r.json());

  const remove = useMutation({
    mutationFn: (ids: string[]) => Promise.all(ids.map(id => post(`/${id}`, { method: 'DELETE' }))),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['cid-blacklist'] }); setSelected(new Set()); },
  });

  function toggle(id: string) {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  }
  function toggleAll() {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map(r => r.id || '').filter(Boolean)));
  }

  function importCsv() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv,text/csv,.txt';
    input.onchange = async () => {
      const f = input.files?.[0];
      if (!f) return;
      const text = await f.text();
      const lines = text.split(/\r?\n/).filter(Boolean);
      // First line could be a header — skip if it looks non-numeric.
      const start = /^[\d+]/.test(lines[0]) ? 0 : 1;
      let n = 0;
      for (const line of lines.slice(start)) {
        const cells = line.match(/"([^"]*)"|[^,]+/g)?.map((c: string) => c.replace(/^"|"$/g, '')) || [];
        const [caller_id, description] = cells;
        if (!caller_id) continue;
        await post('', { method: 'POST', body: JSON.stringify({ caller_id, description: description || '' }) });
        n++;
      }
      qc.invalidateQueries({ queryKey: ['cid-blacklist'] });
      alert(`Imported ${n} entries.`);
    };
    input.click();
  }

  return (
    <div className="space-y-3">
      <h1 className="text-xl font-semibold">CID Blacklist</h1>

      <div className="card p-2 flex flex-wrap gap-1 items-center">
        <button className="btn-primary text-sm inline-flex items-center gap-1" onClick={() => setEditorRow(null)}>
          <Plus size={14} /> Add
        </button>
        <button className="btn-primary text-sm inline-flex items-center gap-1"
                onClick={() => {
                  if (selected.size !== 1) return alert('Select exactly one row to edit.');
                  setEditorRow(filtered.find(r => r.id === [...selected][0]) || null);
                }}>
          <Edit2 size={14} /> Edit
        </button>
        <button className="btn-primary text-sm inline-flex items-center gap-1"
                onClick={() => {
                  if (!selected.size) return alert('Select rows to delete.');
                  if (confirm(`Delete ${selected.size} entry(ies)?`)) remove.mutate([...selected]);
                }}>
          <Trash2 size={14} /> Delete
        </button>
        <button className="btn-primary text-sm inline-flex items-center gap-1" onClick={importCsv}>
          <Upload size={14} /> Import
        </button>
      </div>

      <div className="flex items-center gap-1 bg-elevated rounded-md px-2 py-1.5">
        <Search size={14} className="text-textSecondary" />
        <input className="bg-transparent outline-none text-sm w-full"
               value={search} onChange={e => setSearch(e.target.value)}
               placeholder="Search…" />
      </div>

      <div className="card p-0 overflow-x-auto">
        <table className="table">
          <thead>
            <tr>
              <th className="w-8">
                <input type="checkbox"
                       checked={filtered.length > 0 && selected.size === filtered.length}
                       onChange={toggleAll} />
              </th>
              <th>Blacklisted Caller ID</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(r => (
              <tr key={r.id || r.caller_id}
                  className={`hover:bg-elevated cursor-pointer ${selected.has(r.id || '') ? 'bg-primary/5' : ''}`}
                  onClick={() => setEditorRow(r)}>
                <td onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" checked={selected.has(r.id || '')}
                         onChange={() => r.id && toggle(r.id)} />
                </td>
                <td className="font-mono text-sm">{r.caller_id}</td>
                <td className="text-sm">{r.description}</td>
              </tr>
            ))}
            {!filtered.length && (
              <tr><td colSpan={3} className="text-center py-8 text-textSecondary">No blacklisted callers.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editorRow !== undefined && (
        <CidBlacklistEditor
          row={editorRow}
          onClose={() => setEditorRow(undefined)}
          onSaved={() => { qc.invalidateQueries({ queryKey: ['cid-blacklist'] }); setEditorRow(undefined); }}
        />
      )}
    </div>
  );
}

function CidBlacklistEditor({ row, onClose, onSaved }: { row: CidBlRow | null; onClose: () => void; onSaved: () => void }) {
  const [callerId,    setCallerId]    = useState(row?.caller_id || '');
  const [description, setDescription] = useState(row?.description || '');
  const [busy, setBusy] = useState(false);
  const [err,  setErr]  = useState('');

  async function go() {
    if (!callerId) { setErr('Caller ID required'); return; }
    setBusy(true); setErr('');
    try {
      const body = JSON.stringify({ caller_id: callerId, description });
      const url  = row?.id ? `/api/advanced/cid-blacklist/${row.id}` : '/api/advanced/cid-blacklist';
      const r = await fetch(url, {
        method: row?.id ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body,
      });
      if (!r.ok) throw new Error(await r.text());
      onSaved();
    } catch (e: any) { setErr(String(e.message || e)); }
    finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center" onClick={onClose}>
      <div className="card p-4 w-full max-w-md" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-semibold mb-3">{row?.id ? 'Edit blacklisted Caller ID' : 'Add Caller ID to blacklist'}</h2>
        <div className="space-y-3">
          <Field label="Blacklisted Caller ID *">
            <input className="input font-mono" value={callerId} onChange={e => setCallerId(e.target.value)} placeholder="+18006182247" />
          </Field>
          <Field label="Description">
            <input className="input" value={description} onChange={e => setDescription(e.target.value)} placeholder="e.g. Bothersome Client" />
          </Field>
        </div>
        {err && <div className="text-sm text-danger mt-2">{err}</div>}
        <div className="flex justify-end gap-2 mt-4">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={busy} onClick={go}>{busy ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Secure SIP — 3CX shape: enable toggle + Certificate + Private Key file
// pickers (each row is a filename input + upload arrow + clear X).
// ────────────────────────────────────────────────────────────────────────────

export function SecureSIP() {
  const qc = useQueryClient();
  const rawCfg = useQuery<any>({
    queryKey: ['secure-sip'],
    queryFn: () => fetch('/api/advanced/secure-sip', { headers: { Authorization: `Bearer ${getToken()}` } }).then(r => r.json()),
  }).data;
  const cfg: any = rawCfg && typeof rawCfg === 'object' && !Array.isArray(rawCfg) ? rawCfg : {};

  const [form, setForm] = useState<any>({});
  const f = { ...cfg, ...form };
  const set = (k: string, v: any) => setForm({ ...form, [k]: v });

  const save = useMutation({
    mutationFn: () => fetch('/api/advanced/secure-sip', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
      body: JSON.stringify({
        enabled: !!f.enabled,
        certFile: f.certFile || '',
        keyFile:  f.keyFile  || '',
        certBody: f.certBody || undefined,
        keyBody:  f.keyBody  || undefined,
      }),
    }).then(r => r.json()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['secure-sip'] }),
  });

  function pickFile(target: 'cert' | 'key') {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pem,.crt,.cer,.key,.txt,text/plain';
    input.onchange = async () => {
      const fl = input.files?.[0];
      if (!fl) return;
      const text = await fl.text();
      if (target === 'cert') setForm({ ...form, certFile: fl.name, certBody: text });
      else                   setForm({ ...form, keyFile:  fl.name, keyBody:  text });
    };
    input.click();
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold flex-1">Secure SIP</h1>
        <button className="btn-primary text-sm inline-flex items-center gap-1" onClick={() => save.mutate()}>
          <Save size={14} /> Save
        </button>
      </div>

      <div className="card p-4 space-y-4">
        <label className="inline-flex items-center gap-2 text-sm font-medium">
          <input type="checkbox" checked={!!f.enabled} onChange={e => set('enabled', e.target.checked)} />
          Enable Secure SIP
        </label>

        <div>
          <label className="block text-sm text-textSecondary mb-1">Certificate</label>
          <div className="flex gap-1">
            <input className="input flex-1 font-mono" readOnly
                   value={f.certFile || ''}
                   placeholder="domain_cert_*.pem" />
            <button className="btn-primary text-sm px-2" onClick={() => pickFile('cert')} title="Upload">↑</button>
            <button className="btn-ghost text-sm px-2"   onClick={() => setForm({ ...form, certFile: '', certBody: '' })} title="Clear">×</button>
          </div>
        </div>

        <div>
          <label className="block text-sm text-textSecondary mb-1">Private Key</label>
          <div className="flex gap-1">
            <input className="input flex-1 font-mono" readOnly
                   value={f.keyFile || ''}
                   placeholder="domain_key_*.pem" />
            <button className="btn-primary text-sm px-2" onClick={() => pickFile('key')} title="Upload">↑</button>
            <button className="btn-ghost text-sm px-2"   onClick={() => setForm({ ...form, keyFile: '', keyBody: '' })} title="Clear">×</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Codecs — 3CX shape: two stacked priority lists (LAN + WAN), each row is
// drag-to-reorder with a Priority/Codec table layout.
// ────────────────────────────────────────────────────────────────────────────
const DEFAULT_CODECS_LAN = ['PCMU', 'OPUS', 'G722', 'PCMA', 'G729'];
const DEFAULT_CODECS_WAN = ['PCMU', 'OPUS', 'G722', 'PCMA', 'G729'];

export function Codecs() {
  const qc = useQueryClient();
  const rawCfg = useQuery<any>({ queryKey: ['codecs'], queryFn: () => api.advanced.codecsGet() }).data;
  const cfg: any = rawCfg && typeof rawCfg === 'object' && !Array.isArray(rawCfg) ? rawCfg : {};

  const [form, setForm] = useState<any>({});
  const f = {
    lan_priority: Array.isArray(cfg.lan_priority) && cfg.lan_priority.length ? cfg.lan_priority : DEFAULT_CODECS_LAN,
    wan_priority: Array.isArray(cfg.wan_priority) && cfg.wan_priority.length ? cfg.wan_priority : DEFAULT_CODECS_WAN,
    ...cfg,
    ...form,
  };
  const set = (k: string, v: any) => setForm({ ...form, [k]: v });

  const save = useMutation({
    mutationFn: () => api.advanced.codecsSet(f),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['codecs'] }),
  });

  function reorder(side: 'lan_priority' | 'wan_priority', from: number, to: number) {
    if (from === to) return;
    const arr = [...(f[side] || [])];
    const [item] = arr.splice(from, 1);
    arr.splice(to, 0, item);
    set(side, arr);
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold flex-1">Codecs</h1>
        <button className="btn-primary text-sm inline-flex items-center gap-1" onClick={() => save.mutate()}>
          <Save size={14} /> Save
        </button>
      </div>

      <div className="card p-4 space-y-2">
        <p className="text-sm">Codec order for LAN connected devices or Apps connected to WiFi</p>
        <CodecPriorityTable
          codecs={f.lan_priority}
          onReorder={(from, to) => reorder('lan_priority', from, to)}
        />
      </div>

      <div className="card p-4 space-y-2">
        <p className="text-sm">Codec order for WAN connected devices or Apps connected to LTE/5G</p>
        <CodecPriorityTable
          codecs={f.wan_priority}
          onReorder={(from, to) => reorder('wan_priority', from, to)}
        />
      </div>
    </div>
  );
}

function CodecPriorityTable({
  codecs, onReorder,
}: { codecs: string[]; onReorder: (from: number, to: number) => void }) {
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  return (
    <div className="bg-elevated rounded">
      <div className="grid grid-cols-[40px_80px_1fr] text-xs text-textSecondary px-3 py-2 border-b border-border">
        <span></span>
        <span>Priority</span>
        <span>Codec</span>
      </div>
      {codecs.map((c, i) => (
        <div
          key={c}
          draggable
          onDragStart={() => setDragIdx(i)}
          onDragOver={(e) => e.preventDefault()}
          onDrop={() => {
            if (dragIdx !== null && dragIdx !== i) onReorder(dragIdx, i);
            setDragIdx(null);
          }}
          onDragEnd={() => setDragIdx(null)}
          className={`grid grid-cols-[40px_80px_1fr] items-center px-3 py-2 border-t border-border cursor-move hover:bg-card ${dragIdx === i ? 'opacity-50' : ''}`}
        >
          <span className="text-textSecondary select-none" title="Drag to reorder">⋮⋮</span>
          <span className="text-sm">{i + 1}</span>
          <span className="font-medium">{c}</span>
        </div>
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Parameters (generic KV)
// ────────────────────────────────────────────────────────────────────────────
function KvEditor({ namespace, title, blurb }: { namespace: string; title: string; blurb?: string }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ key_name: '', value: '', description: '', is_secret: false });
  const list = asArray(useQuery<any>({ queryKey: ['kv', namespace], queryFn: () => api.advanced.kvList(namespace) }).data);
  const set = useMutation({ mutationFn: () => api.advanced.kvSet(namespace, form.key_name, form), onSuccess: () => { qc.invalidateQueries({ queryKey: ['kv', namespace] }); setForm({ key_name: '', value: '', description: '', is_secret: false }); } });
  const rm = useMutation({ mutationFn: (key: string) => api.advanced.kvRemove(namespace, key), onSuccess: () => qc.invalidateQueries({ queryKey: ['kv', namespace] }) });

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold flex items-center gap-2"><SettingsIcon /> {title}</h1>
      {blurb && <p className="text-sm text-textSecondary">{blurb}</p>}
      <div className="card p-3 grid grid-cols-5 gap-2 items-end">
        <Field label="Key *"><input className="input font-mono" value={form.key_name} onChange={e => setForm({ ...form, key_name: e.target.value })} /></Field>
        <Field label="Value"><input className="input font-mono" type={form.is_secret ? 'password' : 'text'} value={form.value} onChange={e => setForm({ ...form, value: e.target.value })} /></Field>
        <Field label="Description"><input className="input" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></Field>
        <label className="flex items-center gap-2 pb-2"><input type="checkbox" checked={form.is_secret} onChange={e => setForm({ ...form, is_secret: e.target.checked })} /> Secret</label>
        <button className="btn-primary" onClick={() => set.mutate()} disabled={!form.key_name}><Save size={14}/> Save</button>
      </div>
      <div className="card p-0 overflow-hidden">
        <table className="table">
          <thead><tr><th>Key</th><th>Value</th><th>Description</th><th></th></tr></thead>
          <tbody>
            {list.map((r: any) => (
              <tr key={r.kv_uuid}>
                <td className="font-mono">{r.key_name}</td>
                <td className="font-mono text-xs">{r.value}</td>
                <td className="text-xs text-textSecondary">{r.description}</td>
                <td><button className="btn-ghost p-1 text-danger" onClick={() => rm.mutate(r.key_name)}><Trash2 size={14}/></button></td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={4} className="text-center py-6 text-textSecondary">No entries.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
// Re-export KvEditor for the /system/parameters direct route (raw editor).
export { KvEditor };

// ────────────────────────────────────────────────────────────────────────────
// Parameters — 3CX shape: searchable table of system parameters with Add /
// Edit / Delete. Seed list mirrors the 3CX parameter set 1:1 in name +
// description, with values mapped to Stratus paths and ports. Saved values
// in stratus_ext.system_kv override seed defaults.
// ────────────────────────────────────────────────────────────────────────────

type Param = { name: string; description: string; value: string };

// Seed parameters — names + descriptions follow 3CX's conventions so admins
// switching from 3CX to Stratus see familiar keys. Default values point at
// Stratus/FreeSWITCH paths. Sorted alphabetically.
const PARAMETER_SEEDS: Param[] = [
  { name: 'ALLOWED_REGISTER_FROM',      description: 'Restrict REGISTER allowed from IP/CIDR list',                       value: '' },
  { name: 'ATTENDED_TRANSFER_TIMEOUT',  description: 'Seconds before an attended transfer auto-cancels',                   value: '60' },
  { name: 'BACKUPPATH',                 description: 'Path where backup archives are stored',                              value: '/var/backups/stratus' },
  { name: 'BLIND_TRANSFER_TIMEOUT',     description: 'Seconds before a blind transfer rings back',                         value: '30' },
  { name: 'CALLRECORDSROOT',            description: 'Specify the path to the recordings folder',                          value: '/var/lib/freeswitch/recordings' },
  { name: 'CMEXTERNALADDRESS',          description: 'Call Manager External address',                                      value: '' },
  { name: 'CMEXTERNALINTERFACE',        description: 'Call Manager External interface',                                    value: '' },
  { name: 'COMPANYNAME',                description: 'Company name shown on phones and welcome emails',                    value: '' },
  { name: 'CONFPLACE_MOH_SOURCE',       description: 'Music-on-hold source used for conference rooms',                     value: '/var/lib/stratus/prompts/onhold.wav' },
  { name: 'COUNTRY',                    description: 'Two-letter country code used for default dial plans',                value: 'US' },
  { name: 'DAILYBACKUP',                description: 'Time-of-day for the daily backup job (HH:MM)',                       value: '03:00' },
  { name: 'DBNAME',                     description: 'Postgres database name',                                             value: 'fusionpbx' },
  { name: 'DBPORT',                     description: 'Postgres listen port',                                               value: '5432' },
  { name: 'DBSERVER',                   description: 'Postgres host (use socket path or 127.0.0.1)',                       value: '/var/run/postgresql' },
  { name: 'DEBUGPATH',                  description: 'Path where debug bundles are written',                               value: '/var/log/stratus/debug' },
  { name: 'DEFAULTAUDIOFILE',           description: 'Default audio file used when no prompt is configured',                value: '/var/lib/stratus/prompts/onhold.wav' },
  { name: 'DEFAULTHOLIDAYPROMPT',       description: 'Contains the path to the default holiday prompt',                    value: '/var/lib/stratus/prompts/HolidayDefaultPrompt.wav' },
  { name: 'DEFAULTLOCALIP',             description: 'The default local IP of the PBX',                                    value: '0.0.0.0' },
  { name: 'DIAL_TIMEOUT',               description: 'Default ring time for outbound calls (sec)',                         value: '30' },
  { name: 'DOMAINNAME',                 description: 'Primary FQDN reachable by phones / SBCs',                            value: '' },
  { name: 'ENABLE_AUDIT',               description: '1 = log every admin action to audit_log_v2',                         value: '1' },
  { name: 'ENABLE_AUTH_LOG',            description: '1 = log every login attempt',                                        value: '1' },
  { name: 'ENABLE_INTRA_DOMAIN_ROUTING', description: '1 = allow direct routing between extensions in different domains',  value: '0' },
  { name: 'ENABLE_PEER_TO_PEER',        description: '1 = enable direct media (peer-to-peer RTP) when both ends are LAN',  value: '1' },
  { name: 'ESLPASSWORD',                description: 'FreeSWITCH event-socket password',                                   value: 'ClueCon' },
  { name: 'ESLPORT',                    description: 'FreeSWITCH event-socket port',                                       value: '8021' },
  { name: 'FIRSTEXTPORT',               description: 'First port to use for external calls',                               value: '9000' },
  { name: 'FIRSTLOCALPORT',             description: 'First port to use for internal calls',                               value: '7000' },
  { name: 'GLOBAL_RECORDING_FORMAT',    description: 'Audio format for new recordings (wav, mp3, ogg, opus)',               value: 'wav' },
  { name: 'HOLD_TIMEOUT',               description: 'Seconds before holding caller is reminded',                          value: '60' },
  { name: 'HOTDESK_LOGOUT_TIMEOUT',     description: 'Hot-desk auto-logout timeout (minutes, 0 = never)',                   value: '0' },
  { name: 'HUNTGROUP_RING_DURATION',    description: 'Default ring duration per agent in a hunt group (sec)',               value: '20' },
  { name: 'INST_FAILOVER_OPTIONS',      description: 'Failover options for clustered installs',                            value: '' },
  { name: 'INTERNALCALLRECORD',         description: '1 = record internal calls by default',                               value: '0' },
  { name: 'INTERNAL_DIAL_TIMEOUT',      description: 'Default ring time for internal calls (sec)',                         value: '30' },
  { name: 'IVR_DIRECT_DIALING_GRAMMAR', description: 'Direct dialing grammar accepted by IVRs',                            value: '[0-9]{4}' },
  { name: 'IVR_MOH_SOURCE',             description: 'Music-on-hold source used for IVRs',                                 value: '/var/lib/stratus/prompts/onhold.wav' },
  { name: 'IVR_RTPFIRST',               description: 'RTP range of ports allocated for IVR (first)',                       value: '12000' },
  { name: 'IVR_RTPLAST',                description: 'RTP range of ports allocated for IVR (last)',                        value: '13999' },
  { name: 'IVR_SIPPORT',                description: 'Specifies SIP port for IVR component',                               value: '5483' },
  { name: 'IVRDIALINGTONE',             description: 'Dialing tone audio used by the IVR',                                 value: '/var/lib/stratus/prompts/USProgresstone.wav' },
  { name: 'IVRPROMPTPATH',              description: 'Path for the IVR prompt files',                                      value: '/var/lib/stratus/prompts' },
  { name: 'IVRSIPINTERFACE',            description: 'Binding address of NIC for IVR SIP',                                 value: '127.0.0.1' },
  { name: 'LANGUAGE',                   description: 'Default UI / prompt language code',                                  value: 'en-US' },
  { name: 'LASTEXTPORT',                description: 'Last port to use for external calls',                                 value: '10499' },
  { name: 'LASTLOCALPORT',              description: 'Last port to use for internal calls',                                 value: '8499' },
  { name: 'LICENSEKEY',                 description: 'License key for paid Stratus features',                              value: '' },
  { name: 'LOGSPATH',                   description: 'Path where Stratus writes service logs',                              value: '/var/log/stratus' },
  { name: 'MAINTENANCESCRIPTONSTARTUP', description: 'If set to 0 — forces configuration regeneration on startup',         value: '1' },
  { name: 'MAX_CALLS_PER_EXTENSION',    description: 'Maximum simultaneous calls a single extension can have',             value: '5' },
  { name: 'MS_BLINDEXTPORTS',           description: 'Flag used to inform PBX about blind external port use',               value: '1' },
  { name: 'MSEXTERNALADDRESS',          description: 'Media Server External address',                                      value: '' },
  { name: 'MSEXTERNALINTERFACE',        description: 'Media Server External interface',                                    value: '' },
  { name: 'MUSICONHOLDFILE',            description: 'Music file for music on hold',                                       value: '/var/lib/stratus/prompts/music_only.wav' },
  { name: 'MUSICONHOLDFILE1',           description: 'Music file 1 for music on hold',                                     value: '' },
  { name: 'MUSICONHOLDFILE2',           description: 'Music file 2 for music on hold',                                     value: '' },
  { name: 'MUSICONHOLDFILE3',           description: 'Music file 3 for music on hold',                                     value: '' },
  { name: 'MUSICONHOLDFILE4',           description: 'Music file 4 for music on hold',                                     value: '' },
  { name: 'MUSICONHOLDFILE5',           description: 'Music file 5 for music on hold',                                     value: '' },
  { name: 'PROVISIONFQDN',              description: 'FQDN phones use to fetch provisioning XML',                          value: '' },
  { name: 'PROVISIONINGURL',            description: 'Full URL phones contact for provisioning',                           value: '' },
  { name: 'RECORDING_FORMAT_QUALITY',   description: 'Audio quality preset (low/mid/high)',                                value: 'high' },
  { name: 'RECORDING_RETENTION_DAYS',   description: 'Days to keep recordings before auto-purge (0 = forever)',             value: '90' },
  { name: 'RING_TIMEOUT',               description: 'Default per-ring timeout (sec) before next strategy step',            value: '20' },
  { name: 'SBCFQDN',                    description: 'FQDN of the SBC component',                                          value: '' },
  { name: 'SECURESIPPORT',              description: 'TLS SIP listen port',                                                value: '5061' },
  { name: 'SIPPORT',                    description: 'UDP/TCP SIP listen port',                                            value: '5060' },
  { name: 'SUBSCRIPTIONEMAIL',          description: 'Email address tied to license/subscription',                         value: '' },
  { name: 'TIMEZONE',                   description: 'Server timezone (TZ database name)',                                  value: 'America/New_York' },
  { name: 'TRANSCRIPTION_PROVIDER',     description: 'Provider used for call recording transcription',                     value: 'deepgram' },
  { name: 'TTS_PROVIDER',               description: 'Provider used for text-to-speech',                                   value: 'openai' },
  { name: 'TUNNELSPORT',                description: 'SBC tunnel port',                                                    value: '5090' },
  { name: 'VOICEMAILMAXLENGTH',         description: 'Max voicemail recording length (sec)',                               value: '180' },
  { name: 'VOICEMAILRETRIES',           description: 'Times voicemail prompt is retried before hangup',                    value: '3' },
  { name: 'VOICEMAIL_TIMEZONE',         description: 'Timezone used for voicemail timestamps in transcripts',              value: 'America/New_York' },
  { name: 'WEBMEETINGSURL',             description: 'Public URL for web-meeting room landing pages',                      value: '' },
  { name: 'WEBSERVERPORT',              description: 'Web admin HTTP listen port',                                          value: '3500' },
  { name: 'WEBSERVERSECUREPORT',        description: 'Web admin HTTPS listen port',                                         value: '443' },

  // ── SBC / Tunnel (mined from INSTALL-RE-FINDINGS-2 + RESEARCH-FINDINGS-2) ──
  { name: 'SBC_RECONNECT_INTERVAL',     description: 'Seconds between SBC tunnel reconnect attempts',                      value: '30' },
  { name: 'SBC_PRINT_STATS_INTERVAL',   description: 'Seconds between SBC stats log entries',                              value: '30' },
  { name: 'SBC_FAILOVER_INTERVAL',      description: 'Seconds before SBC switches to secondary tunnel target',             value: '120' },
  { name: 'SBC_GIVEUP_TIMEOUT',         description: 'Seconds before SBC abandons reconnect and exits',                    value: '300' },
  { name: 'SBC_LOG_LEVEL',              description: 'SBC log verbosity (NONE/EMERG/ALERT/CRIT/ERR/WARN/NOTICE/INFO/DEBUG)', value: 'INFO' },
  { name: 'SBC_LOG_TYPE',               description: 'SBC log sink (cout, cerr, file, syslog)',                            value: 'syslog' },
  { name: 'SBC_LOG_FILE',               description: 'SBC log file path',                                                  value: 'Logs/stratus-sbc.log' },
  { name: 'SBC_LOG_SIZE',               description: 'SBC log rotation size',                                              value: '20M' },
  { name: 'SBC_SECURITY_MODE',          description: 'SBC transport mode (0=TCP, 1=TLS)',                                  value: '1' },
  { name: 'SBC_TUNNEL_ADDR2',           description: 'Secondary SBC tunnel host for passive failover',                     value: '' },
  { name: 'SBC_TUNNEL_PORT2',           description: 'Secondary SBC tunnel port for passive failover',                     value: '' },
  { name: 'SBC_FAILOVER_ENABLE',        description: 'Enable SBC-level passive failover (separate from SIP dispatcher)',   value: 'false' },
  { name: 'SBC_TLS_ONLY',               description: 'Reject UDP/TCP signaling on this SBC, force TLS',                    value: 'true' },
  { name: 'SBC_CERT_EXPIRY_ALERT_DAYS', description: 'Days ahead of cert expiry to alert admin',                           value: '30' },
  { name: 'SBC_HEARTBEAT_DEBOUNCE_SECONDS', description: 'Seconds an SBC must be down before alerting',                    value: '60' },
  { name: 'SBC_UPDATE_FEED_URL',        description: 'Pull-based SBC update channel XML feed',                             value: '/api/sbc/sbc.xml' },
  { name: 'SBC_LOCAL_SIP_ADDR',         description: 'SBC local SIP bind address (0.0.0.0 = auto-detect)',                 value: '0.0.0.0' },
  { name: 'SBC_LOCAL_SIP_PORT',         description: 'SBC local SIP bind port',                                            value: '5060' },
  { name: 'SBC_NUM_RTP_PORTS',          description: 'Number of RTP ports the SBC reserves locally',                       value: '200' },
  { name: 'SBC_INTERNAL_BRIDGE_PORT',   description: 'Internal SIP bridge port between MediaServer and Kamailio',          value: '5080' },
  { name: 'TUNNEL_ID_PARAM',            description: 'SIP Via branch param used to discriminate SBC tunnels',              value: 'tnlid' },
  { name: 'PROVISION_LINK_TTL_HOURS',   description: 'TTL for one-time SBC/extension provisioning secret',                 value: '1' },

  // ── Provisioning / PnP (mined from RESEARCH-FINDINGS-2 PnP RE) ──
  { name: 'PROVISIONING_URL_PATH',         description: 'Per-device signed provisioning URL path template',                value: '/p/<secret>/cfg<mac>.xml' },
  { name: 'PROVISIONING_HMAC_SECRET',      description: 'HMAC key for signed provisioning URLs',                           value: '' },
  { name: 'PROVISIONING_URL_EXPIRY_SECONDS', description: 'TTL for HMAC-signed prov URL queries',                          value: '3600' },
  { name: 'PROVISIONING_FETCH_COUNT_MAX',  description: 'Max times a single signed prov URL can be fetched',               value: '3' },
  { name: 'OPTIONS_PING_INTERVAL',         description: 'Active SIP OPTIONS keepalive interval (sec) to registered phones', value: '60' },
  { name: 'OPTIONS_PING_FAIL_THRESHOLD',   description: 'Consecutive OPTIONS failures before marking phone offline',       value: '3' },
  { name: 'PROV_REFRESH_FANVIL_MIN',       description: 'Phonebook/config refresh interval for Fanvil phones (minutes)',    value: '7200' },
  { name: 'PROV_REFRESH_YEALINK_MIN',      description: 'Phonebook/config refresh interval for Yealink phones (minutes)',   value: '7200' },
  { name: 'PROV_REFRESH_GRANDSTREAM_MIN',  description: 'Phonebook/config refresh interval for Grandstream phones (min)',   value: '720' },
  { name: 'PROV_REFRESH_SNOM_MIN',         description: 'Phonebook/config refresh interval for Snom phones (minutes)',      value: '86400' },
  { name: 'PROV_TFTP_ENABLED',             description: 'Allow TFTP (UDP/69) phone config fetches',                         value: 'false' },
  { name: 'PROV_SIP_MULTICAST_ADDR',       description: 'PnP SIP multicast discovery address',                              value: '224.0.1.75' },
  { name: 'PROV_VENDOR_NAT_KEEPALIVE',     description: 'Per-vendor SIP NAT keepalive interval (seconds)',                  value: '15' },
  { name: 'PROV_NAT_MAXLOST',              description: 'NAT keepalive misses before phone resets registration',            value: '5' },
  { name: 'REGISTER_EXPIRES_DEFAULT',      description: 'SIP REGISTER expires header sent on 200 OK (seconds)',             value: '300' },

  // ── FreeSWITCH media / RTP / codec ──
  { name: 'RTP_RANGE_START',          description: 'Start of RTP UDP port range',                                          value: '7000' },
  { name: 'RTP_RANGE_END',            description: 'End of RTP UDP port range',                                            value: '10999' },
  { name: 'JITTERBUFFER_MSEC',        description: 'FreeSWITCH adaptive jitter buffer (initial:p25:p50)',                  value: '2p:25p:50p' },
  { name: 'OPUS_FEC',                 description: 'Enable Opus forward error correction',                                 value: 'true' },
  { name: 'OPUS_DTX',                 description: 'Enable Opus discontinuous transmission (comfort noise)',               value: 'false' },
  { name: 'OPUS_PACKET_LOSS_PERCENT', description: 'Expected loss hint to Opus encoder',                                   value: '10' },
  { name: 'OPUS_KEEP_FEC_ENABLED',    description: 'Force-keep Opus FEC even when peer disables',                          value: '1' },
  { name: 'OPUS_VBR',                 description: 'Use Opus variable bitrate for handoff resilience',                     value: 'on' },
  { name: 'OPUS_CBR',                 description: 'Use Opus constant bitrate (off recommended on Starlink)',              value: 'false' },
  { name: 'RTPENGINE_MEDIA_TIMEOUT',  description: 'rtpengine media-timeout in seconds',                                   value: '600' },
  { name: 'RTPENGINE_MODE',           description: 'rtpengine mode (passthrough, transcoding)',                            value: 'transcoding' },
  { name: 'RTCP_FB_NACK',             description: 'Announce RTCP NACK feedback in SDP',                                   value: 'true' },

  // ── Kamailio / SIP routing ──
  { name: 'SIP_TLS_PORT',                  description: 'SIP TLS listen port',                                              value: '5061' },
  { name: 'KAM_DISPATCHER_HEALTH_INTERVAL', description: 'Seconds between OPTIONS health pings on dispatcher members',      value: '30' },
  { name: 'KAM_DISPATCHER_PRIMARY_SET',    description: 'Dispatcher set ID for primary trunk pool',                         value: '1' },
  { name: 'KAM_DISPATCHER_BACKUP_SET',     description: 'Dispatcher set ID for backup trunk pool',                          value: '2' },
  { name: 'TOPOH_HIDE_VIA',                description: 'Hide internal Via headers via Kamailio topoh',                     value: 'true' },

  // ── Trunk template / Gateway (mined from RESEARCH-FINDINGS-3 §1) ──
  { name: 'TRUNK_DEFAULT_PASSWORD_LENGTH',         description: 'Random trunk password length on auto-generate',            value: '12' },
  { name: 'TRUNK_TIME_BETWEEN_REGISTRATION',       description: 'Default REGISTER refresh interval per trunk (seconds)',    value: '600' },
  { name: 'TRUNK_DEFAULT_SIMULTANEOUS_CALLS',      description: 'Default per-trunk concurrent call cap',                    value: '10' },
  { name: 'TRUNK_DEFAULT_MATCHING_STRATEGY',       description: 'Inbound trunk match (MatchAnyFields, MatchProxyHost, …)',  value: 'MatchAnyFields' },
  { name: 'TRUNK_DEFAULT_REQUIRE_AUTH_FOR',        description: 'When to require trunk auth (Nothing/Inbound/Outbound/Both)', value: 'Nothing' },
  { name: 'TRUNK_DEFAULT_SRTP_MODE',               description: 'Default SRTP policy for new trunks',                       value: 'SRTPDisabled' },
  { name: 'TRUNK_DEFAULT_IP_RESTRICTION',          description: 'Trunk IP family restriction (ANY, IPV4, IPV6)',            value: 'IPV4' },
  { name: 'TRUNK_TEST_PROBE_TIMEOUT',              description: 'Seconds for RunAnalysis trunk test probe',                 value: '50' },

  // ── Queues / Call Center ──
  { name: 'QUEUE_DEFAULT_RING_TIMEOUT',     description: 'Per-cycle ring duration in seconds',                              value: '20' },
  { name: 'QUEUE_DEFAULT_MASTER_TIMEOUT',   description: 'Total seconds caller may stay in queue before DNA',               value: '300' },
  { name: 'QUEUE_DEFAULT_WRAPUP_TIME',      description: 'Agent post-call unavailable seconds (atomic across queues)',     value: '0' },
  { name: 'QUEUE_DEFAULT_SLA_TIME',         description: 'Seconds before SLA breach notification fires',                   value: '120' },
  { name: 'QUEUE_ANNOUNCE_INTERVAL_MIN',    description: 'Lower bound for queue position announcement interval',           value: '10' },
  { name: 'QUEUE_ANNOUNCE_INTERVAL_MAX',    description: 'Upper bound for queue position announcement interval',           value: '1800' },
  { name: 'QUEUE_CALLBACK_ENABLE_TIME',     description: 'Wait seconds before offering callback (-1 = disabled)',          value: '-1' },
  { name: 'QUEUE_CALLBACK_PREFIX',          description: 'Outbound rule prefix used for queue callbacks',                  value: '' },
  { name: 'QUEUE_NO_AGENTS_BEHAVIOR',       description: 'Action when no agents (immediate_dna, hold_then_dna, hold_forever)', value: 'immediate_dna' },
  { name: 'QUEUE_RECORDING_MODE',           description: 'Per-queue recording (Disabled, AllowToOptOut, AskToOptIn)',      value: 'Disabled' },
  { name: 'QUEUE_RESET_STATS_CRON',         description: 'Cron expression for nightly queue stats reset',                  value: '0 0 * * *' },
  { name: 'AUTO_QUEUE_LOGOUT',              description: 'Auto-log-out agents when tenant office hours end',               value: 'true' },

  // ── IVR / Receptionist ──
  { name: 'IVR_DEFAULT_TIMEOUT_SECONDS', description: 'Default DTMF wait timeout on IVR menus',                            value: '5' },
  { name: 'IVR_TRANSFER_ENABLE',         description: 'Allow callers to transfer to any extension from IVR',               value: 'false' },
  { name: 'IVR_PIN_PROTECT',             description: 'Require PIN to enter IVR menu (after-hours emergency line)',        value: 'false' },

  // ── Voicemail / PIN policy ──
  { name: 'VOICEMAIL_PIN_LOCKOUT_ATTEMPTS', description: 'Wrong-PIN attempts before voicemail lockout',                    value: '3' },
  { name: 'VOICEMAIL_PIN_LOCKOUT_MINUTES',  description: 'Voicemail lockout duration after wrong-PIN threshold',          value: '15' },
  { name: 'HOTDESK_PIN_LOCKOUT_ATTEMPTS',   description: 'Wrong-PIN attempts for hot-desk login (separate from VM)',      value: '3' },
  { name: 'PIN_PROTECT_TIMEOUT_DEFAULT',    description: 'Default extension PIN-protect window (seconds)',                value: '60' },

  // ── Hot-desking ──
  { name: 'HOTDESK_EXT_PREFIX',         description: 'Prefix used for stub hot-desk extensions',                          value: 'HD' },
  { name: 'HOTDESK_FORCE_LOGOUT_CRON',  description: 'Cron for nightly force-logout sweep of stale hot-desk sessions',    value: '0 23 * * *' },

  // ── Audit log ──
  { name: 'AUDITLOG_ENABLE',         description: 'Master switch for audit logging',                                     value: 'true' },
  { name: 'AUDITLOG_BACKLOG_MONTHS', description: 'Months of audit log retention before purge',                          value: '12' },
  { name: 'AUDITLOG_PURGE_ENABLE',   description: 'Allow scheduled purge of old audit rows',                             value: 'true' },
  { name: 'AUDITLOG_SHOWPASSWORD',   description: 'Reveal masked password fields in audit diffs (admin only)',           value: 'false' },
  { name: 'AUDITLOG_HASH_CHAIN',     description: 'Enable HMAC hash-chain tamper-protection on audit rows',              value: 'true' },

  // ── Backup ──
  { name: 'BACKUP_FILENAME',           description: 'Default filename for scheduled backup zip',                          value: 'StratusScheduledBackup.zip' },
  { name: 'BACKUP_RETAIN_COUNT',       description: 'Number of rotating backup files to keep on disk',                    value: '10' },
  { name: 'BACKUP_PBKDF2_ITERATIONS',  description: 'PBKDF2 iterations for backup encryption',                            value: '600000' },
  { name: 'BACKUP_HMAC_FOOTER',        description: 'Append HMAC-SHA256 footer to backups for integrity check',           value: 'true' },
  { name: 'BACKUP_INCLUDE_RECORDINGS', description: 'Include call recordings in scheduled backup',                        value: 'false' },
  { name: 'BACKUP_INCLUDE_VOICEMAILS', description: 'Include voicemail audio in scheduled backup',                        value: 'false' },
  { name: 'BACKUP_INCLUDE_PROMPTS',    description: 'Include uploaded prompts in scheduled backup',                       value: 'false' },
  { name: 'BACKUP_INCLUDE_CALL_HISTORY', description: 'Include CDR rows in scheduled backup',                             value: 'true' },
  { name: 'BACKUP_INCLUDE_CERTS',      description: 'Include FQDN/TLS certs in scheduled backup',                         value: 'true' },

  // ── Updates / Maintenance ──
  { name: 'UPDATE_CHECK_GENERAL_HOURS',  description: 'Hours between general update checks',                              value: '2' },
  { name: 'UPDATE_CHECK_FIRMWARE_HOURS', description: 'Hours between phone-firmware update checks',                       value: '2' },
  { name: 'UPDATE_INSTALL_DAY',          description: 'Weekly auto-install day for general/PBX/security updates',         value: 'sunday' },
  { name: 'UPDATE_INSTALL_TIME',         description: 'Local time for weekly auto-install window',                        value: '07:00' },
  { name: 'OFFICE365_SYNC_TIME',         description: 'Daily Microsoft 365 calendar/contact sync local time',             value: '05:00' },

  // ── Alerts (system thresholds) ──
  { name: 'ALERT_CPU_THRESHOLD_PCT',     description: 'CPU pct that triggers Stratus alert',                              value: '90' },
  { name: 'ALERT_RAM_THRESHOLD_PCT',     description: 'RAM pct that triggers Stratus alert',                              value: '90' },
  { name: 'ALERT_SWAP_THRESHOLD_PCT',    description: 'Swap pct that triggers Stratus alert',                             value: '50' },
  { name: 'ALERT_DISK_THRESHOLD_PCT',    description: 'Disk pct that triggers Stratus alert',                             value: '85' },
  { name: 'ALERT_AUTO_RESTART_SERVICES', description: 'Auto-restart Stratus services when alert thresholds hit',          value: 'false' },

  // ── Talk / Meet (WebRTC) ──
  { name: 'TALK_GUEST_RATE_LIMIT_PER_IP',     description: 'Max new guest WebRTC sessions per IP per minute',             value: '5' },
  { name: 'TALK_REQUIRE_TURNSTILE',           description: 'Require Cloudflare Turnstile on Talk/Meet join page',         value: 'false' },
  { name: 'MEET_PERSONAL_ROOM_REQUIRE_OWNER', description: 'Personal Meet rooms require owner present before guests join', value: 'true' },
  { name: 'MEET_SLUG_HMAC_SIGNING',           description: 'Sign Meet slugs with HMAC + expiry (vs raw slug)',            value: 'true' },

  // ── HIPAA / Compliance ──
  { name: 'HIPAA_MODE',              description: 'Force local-only AI/transcription, audit every recording access',     value: 'false' },
  { name: 'KARI_LAW_GATE',           description: "Hard-gate tenant onboarding on E911 dispatchable-location entry",     value: 'true' },
  { name: 'STIR_SHAKEN_ENABLE',      description: 'Enable in-base STIR/SHAKEN signing on outbound calls',                 value: 'true' },
  { name: 'STIR_SHAKEN_ATTESTATION', description: 'Default attestation level when signing (A, B, C)',                    value: 'A' },

  // ── Firewall (rate-limit) ──
  { name: 'FW_SSH_RATE_LIMIT_PER_MIN',  description: 'SSH brute-force rate limit (connections/minute)',                  value: '15' },
  { name: 'FW_ICMP_RATE_LIMIT_PER_SEC', description: 'ICMP echo rate limit (per second)',                                value: '1' },

  // ── Push gateway ──
  { name: 'PUSH_APNS_FORCE_IPV4',  description: 'Force IPv4 outbound to api.push.apple.com',                              value: 'true' },
  { name: 'PUSH_ACK_P95_TARGET_MS', description: 'Target p95 push-sent → INVITE-arrived latency for telemetry alarm',    value: '2000' },

  // ── AI / Transcription ──
  { name: 'AI_DEFAULT_PROVIDER',    description: 'Default ASR/transcription provider (deepgram, openai, assemblyai, whisper_local)', value: 'deepgram' },
  { name: 'AI_REALTIME_PROVIDER',   description: 'Real-time conversational agent provider (openai_realtime, deepgram, none)',       value: 'deepgram' },
  { name: 'AI_FREESWITCH_MODULE',   description: 'FreeSWITCH module used for live audio bridge',                                     value: 'mod_audio_stream' },
];

export function Parameters() {
  const qc = useQueryClient();
  const list = asArray(useQuery<any>({ queryKey: ['kv', 'parameters'], queryFn: () => api.advanced.kvList('parameters') }).data);

  const [search, setSearch]       = useState('');
  const [page, setPage]           = useState(1);
  const [editorRow, setEditorRow] = useState<Param | null | undefined>(undefined);
  const pageSize = 25;

  // Saved KV entries override seeds with the same name.
  const savedByName = new Map<string, any>();
  for (const r of list) savedByName.set(r.key_name, r);
  const merged: Param[] = PARAMETER_SEEDS.map(s => {
    const saved = savedByName.get(s.name);
    return saved
      ? { name: s.name, description: saved.description || s.description, value: saved.value ?? s.value }
      : s;
  });
  // Add any saved-only params (admin-defined keys not in the seed list).
  for (const r of list) {
    if (!PARAMETER_SEEDS.some(s => s.name === r.key_name)) {
      merged.push({ name: r.key_name, description: r.description || '', value: r.value || '' });
    }
  }
  merged.sort((a, b) => a.name.localeCompare(b.name));

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return merged.filter(p =>
      !q || `${p.name} ${p.description} ${p.value}`.toLowerCase().includes(q)
    );
  }, [merged, search]);
  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const visible = filtered.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => { setPage(1); }, [search]);

  const remove = useMutation({
    mutationFn: (name: string) => api.advanced.kvRemove('parameters', name),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['kv', 'parameters'] }),
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold flex-1">Parameters</h1>
        <button className="btn-primary text-sm inline-flex items-center gap-1" onClick={() => setEditorRow(null)}>
          <Plus size={14} /> Add
        </button>
      </div>

      <div className="text-sm text-textSecondary">Custom Parameters</div>

      <div className="flex items-center gap-1 bg-elevated rounded-md px-2 py-1.5">
        <Search size={14} className="text-textSecondary" />
        <input className="bg-transparent outline-none text-sm w-full"
               value={search} onChange={e => setSearch(e.target.value)}
               placeholder="Search …" />
      </div>

      <div className="card p-0 overflow-x-auto">
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Description</th>
              <th>Value</th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {visible.map(p => (
              <tr key={p.name} className="hover:bg-elevated cursor-pointer" onClick={() => setEditorRow(p)}>
                <td className="font-mono text-sm">{p.name}</td>
                <td className="text-sm text-textSecondary truncate max-w-md" title={p.description}>{p.description}</td>
                <td className="text-sm font-mono truncate max-w-md" title={p.value}>{p.value}</td>
                <td onClick={e => e.stopPropagation()}>
                  {savedByName.has(p.name) && (
                    <button className="btn-ghost p-1 text-textSecondary hover:text-danger"
                            onClick={() => confirm(`Reset ${p.name} to default?`) && remove.mutate(p.name)}
                            title="Reset to default">
                      <Trash2 size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {!visible.length && (
              <tr><td colSpan={4} className="text-center py-8 text-textSecondary">No parameters match.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {pages > 1 && <SimplePagination page={page} pages={pages} onPage={setPage} />}

      {editorRow !== undefined && (
        <ParameterEditor
          row={editorRow}
          onClose={() => setEditorRow(undefined)}
          onSaved={() => { qc.invalidateQueries({ queryKey: ['kv', 'parameters'] }); setEditorRow(undefined); }}
        />
      )}
    </div>
  );
}

function ParameterEditor({ row, onClose, onSaved }: { row: Param | null; onClose: () => void; onSaved: () => void }) {
  const [name,        setName]        = useState(row?.name || '');
  const [description, setDescription] = useState(row?.description || '');
  const [value,       setValue]       = useState(row?.value || '');
  const [busy, setBusy] = useState(false);
  const [err,  setErr]  = useState('');

  async function go() {
    if (!name) { setErr('Name required'); return; }
    setBusy(true); setErr('');
    try {
      await api.advanced.kvSet('parameters', name, { value, description });
      onSaved();
    } catch (e: any) { setErr(String(e.message || e)); }
    finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center" onClick={onClose}>
      <div className="card p-4 w-full max-w-xl" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-semibold mb-3">{row ? `Edit ${row.name}` : 'Add Parameter'}</h2>
        <div className="space-y-3">
          <Field label="Name *">
            <input className="input font-mono" value={name} onChange={e => setName(e.target.value)}
                   placeholder="MY_PARAM_NAME" disabled={!!row} />
          </Field>
          <Field label="Description">
            <input className="input" value={description} onChange={e => setDescription(e.target.value)} />
          </Field>
          <Field label="Value">
            <input className="input font-mono" value={value} onChange={e => setValue(e.target.value)} />
          </Field>
        </div>
        {err && <div className="text-sm text-danger mt-2">{err}</div>}
        <div className="flex justify-end gap-2 mt-4">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={busy} onClick={go}>{busy ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}
export function SystemPrompts() { return <KvEditor namespace="system_prompts" title="System Prompts" blurb="Default audio prompts. Per-tenant overrides live under Departments." />; }

// ────────────────────────────────────────────────────────────────────────────
// Templates — 4-tab editor (Phone Templates / DECT/FXS Templates / Provider
// Templates / Email Templates). Each tab shows a template-name dropdown +
// Add / Copy / Delete / Save toolbar over a monospace XML/text editor.
// ────────────────────────────────────────────────────────────────────────────

type Tpl = {
  template_uuid: string;
  template_name: string;
  vendor_key?: string | null;
  insert_point?: string;
  xml_content?: string;
  enabled?: boolean;
  description?: string | null;
  category?: 'phone' | 'dect_fxs' | 'provider' | 'email';
};

const TPL_TABS: { v: Tpl['category']; label: string }[] = [
  { v: 'phone',     label: 'Phone Templates' },
  { v: 'dect_fxs',  label: 'DECT / FXS Templates' },
  { v: 'provider',  label: 'Provider Templates' },
  { v: 'email',     label: 'Email Templates' },
];

export function XMLTemplates() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tpl['category']>('phone');
  const [selected, setSelected] = useState<string>('');
  const [draft, setDraft] = useState<string>('');
  const [fullScreen, setFullScreen] = useState(false);

  const list: Tpl[] = asArray(useQuery<any>({
    queryKey: ['xml-templates'],
    queryFn: () => api.advanced.xmlList(),
  }).data);

  // Templates in the active tab (legacy rows without `category` are 'phone').
  const inTab = list.filter(t => (t.category || 'phone') === tab);

  // Auto-pick first when tab changes or list reloads
  useEffect(() => {
    if (!selected || !inTab.some(t => t.template_uuid === selected)) {
      setSelected(inTab[0]?.template_uuid || '');
    }
  }, [tab, list.length]);

  const current = inTab.find(t => t.template_uuid === selected);
  useEffect(() => {
    setDraft(current?.xml_content || '');
  }, [current?.template_uuid]);

  const dirty = !!current && draft !== (current.xml_content || '');

  const save = useMutation({
    mutationFn: () => current
      ? api.advanced.xmlUpdate(current.template_uuid, { xml_content: draft })
      : Promise.resolve(null),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['xml-templates'] }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.advanced.xmlRemove(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['xml-templates'] }); setSelected(''); },
  });

  function onAdd() {
    const name = prompt(`New ${TPL_TABS.find(t => t.v === tab)?.label} — name?`, '');
    if (!name) return;
    api.advanced.xmlAdd({
      template_name: name,
      vendor_key: '',
      insert_point: 'replace',
      xml_content: tab === 'email' ? '' : '<?xml version="1.0"?>\n',
      enabled: true,
      description: '',
      category: tab,
    } as any).then(() => qc.invalidateQueries({ queryKey: ['xml-templates'] }));
  }

  function onCopy() {
    if (!current) return;
    const name = prompt('Copy as — new name?', `${current.template_name}_copy`);
    if (!name) return;
    api.advanced.xmlAdd({
      template_name: name,
      vendor_key: current.vendor_key,
      insert_point: current.insert_point || 'replace',
      xml_content: draft,
      enabled: true,
      description: current.description || '',
      category: tab,
    } as any).then(() => qc.invalidateQueries({ queryKey: ['xml-templates'] }));
  }

  function onDelete() {
    if (!current) return;
    if (!confirm(`Delete template "${current.template_name}"? This cannot be undone.`)) return;
    remove.mutate(current.template_uuid);
  }

  return (
    <div className="space-y-3">
      <h1 className="text-xl font-semibold">Templates</h1>

      {/* Tabs — Phone / DECT-FXS / Provider / Email */}
      <div className="flex gap-1 border-b border-border">
        {TPL_TABS.map(t => (
          <button key={t.v}
                  onClick={() => setTab(t.v)}
                  className={`px-4 py-2 text-sm whitespace-nowrap ${
                    tab === t.v
                      ? 'border-b-2 border-primary text-primary'
                      : 'text-textSecondary hover:text-textPrimary'
                  }`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Template-name dropdown */}
      <select className="input" value={selected} onChange={e => setSelected(e.target.value)}>
        {!inTab.length && <option value="">(no templates)</option>}
        {inTab.map(t => (
          <option key={t.template_uuid} value={t.template_uuid}>
            {t.template_name}
            {t.vendor_key ? ` — ${t.vendor_key}` : ''}
          </option>
        ))}
      </select>

      {/* Toolbar — Add / Copy / Delete / Save / Seed defaults */}
      <div className="card p-2 flex flex-wrap gap-1 items-center">
        <button className="btn-primary text-sm inline-flex items-center gap-1" onClick={onAdd}>
          <Plus size={14} /> Add
        </button>
        <button className="btn-primary text-sm inline-flex items-center gap-1" disabled={!current} onClick={onCopy}>
          <FileText size={14} /> Copy
        </button>
        <button className="btn-primary text-sm inline-flex items-center gap-1" disabled={!current} onClick={onDelete}>
          <Trash2 size={14} /> Delete
        </button>
        <button className="btn-primary text-sm inline-flex items-center gap-1"
                disabled={!current || !dirty || save.isPending}
                onClick={() => save.mutate()}>
          <Save size={14} /> {save.isPending ? 'Saving…' : 'Save'}
        </button>
        <div className="flex-1" />
        <button
          className="btn-ghost text-sm inline-flex items-center gap-1"
          title="Insert the bundled vendor templates (Grandstream DP750/WP820, Yealink T46U/T54W, Fanvil X4U/X3SP, Snom D785/D717, Polycom VVX 411, Cisco 7841, Snom M700, Yealink W60B, Grandstream HT812, Twilio/Telnyx/Voxtelesys/Bandwidth/Flowroute/Teams DR, plus 5 email templates)"
          onClick={async () => {
            if (!confirm(`Seed default ${TPL_TABS.find(t => t.v === tab)?.label} from the bundled library? Existing templates with the same name are skipped.`)) return;
            const r = await fetch('/api/advanced/xml-templates/seed-defaults', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
              body: JSON.stringify({ category: tab }),
            }).then(r => r.json());
            qc.invalidateQueries({ queryKey: ['xml-templates'] });
            alert(`Inserted ${r.inserted ?? 0}, skipped ${r.skipped ?? 0}.`);
          }}>
          Seed defaults
        </button>
      </div>

      {/* Editor */}
      <div className={`card p-0 relative ${fullScreen ? 'fixed inset-0 z-50 m-0 rounded-none' : ''}`}>
        <button className="btn-ghost text-xs absolute right-2 top-2 z-10"
                onClick={() => setFullScreen(f => !f)}>
          {fullScreen ? '⤓ Exit Full Screen' : '⤢ Full Screen'}
        </button>
        <textarea
          className="block w-full bg-card font-mono text-xs leading-relaxed p-3 outline-none resize-none"
          style={{ minHeight: fullScreen ? '100vh' : '60vh' }}
          spellCheck={false}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          placeholder={current ? '' : '— Select or add a template to start editing —'}
          disabled={!current}
        />
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// CDR — 3CX shape: master enable + log-type dropdown + remove-comma-delimiters.
// Persists to /api/advanced/cdr (file-backed JSON, routes/advanced.js).
// ────────────────────────────────────────────────────────────────────────────

const CDR_LOG_TYPES = [
  'Single file for all Calls',
  'One file per Call',
  'Both single + per-call files',
];

export function CDRSettings() {
  const qc = useQueryClient();
  const toast = useToast();
  const rawCfg = useQuery<any>({
    queryKey: ['cdr-settings'],
    queryFn: () => fetch('/api/advanced/cdr', {
      headers: { Authorization: `Bearer ${getToken()}` },
    }).then(r => {
      if (!r.ok) throw new Error(`Failed to load CDR settings (${r.status})`);
      return r.json();
    }),
  }).data;
  const cfg: any = rawCfg && typeof rawCfg === 'object' && !Array.isArray(rawCfg) ? rawCfg : {};

  const [form, setForm] = useState<any>({});
  const f = {
    enabled:               cfg.enabled !== false,
    log_type:              CDR_LOG_TYPES[0],
    remove_comma_delim:    false,
    ...cfg,
    ...form,
  };
  const set = (k: string, v: any) => setForm({ ...form, [k]: v });

  const save = useMutation({
    mutationFn: () => fetch('/api/advanced/cdr', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
      body: JSON.stringify(f),
    }).then(r => {
      if (!r.ok) throw new Error(`Save failed (${r.status})`);
      return r.json();
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['cdr-settings'] }); toast('success', 'CDR settings saved'); },
    onError: (e: any) => toast('error', e?.message || 'Failed to save CDR settings'),
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold flex-1">Call Data Records</h1>
        <button className="btn-primary text-sm inline-flex items-center gap-1" onClick={() => save.mutate()}>
          <Save size={14} /> Save
        </button>
      </div>

      <div className="card p-4 space-y-4">
        <label className="inline-flex items-center gap-2 text-sm font-medium">
          <input type="checkbox" checked={!!f.enabled}
                 onChange={e => set('enabled', e.target.checked)} />
          Enable CDR
        </label>

        <Field label="CDR log type">
          <select className="input" value={f.log_type}
                  onChange={e => set('log_type', e.target.value)}
                  disabled={!f.enabled}>
            {CDR_LOG_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>

        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!!f.remove_comma_delim}
                 onChange={e => set('remove_comma_delim', e.target.checked)}
                 disabled={!f.enabled} />
          Remove comma delimiters
        </label>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// E164 — 3CX shape: country picker + intl-dial code, "Remove if same country"
// + area/national codes, "Remove if same area code", optional prepend prefix.
// ────────────────────────────────────────────────────────────────────────────

const E164_COUNTRIES: { name: string; cc: string }[] = [
  { name: 'United States',         cc: '1' },
  { name: 'Canada',                cc: '1' },
  { name: 'United Kingdom',        cc: '44' },
  { name: 'Australia',             cc: '61' },
  { name: 'Germany',               cc: '49' },
  { name: 'France',                cc: '33' },
  { name: 'Italy',                 cc: '39' },
  { name: 'Spain',                 cc: '34' },
  { name: 'Netherlands',           cc: '31' },
  { name: 'Belgium',               cc: '32' },
  { name: 'Switzerland',           cc: '41' },
  { name: 'Austria',               cc: '43' },
  { name: 'Ireland',               cc: '353' },
  { name: 'Sweden',                cc: '46' },
  { name: 'Norway',                cc: '47' },
  { name: 'Denmark',               cc: '45' },
  { name: 'Finland',               cc: '358' },
  { name: 'Poland',                cc: '48' },
  { name: 'Portugal',              cc: '351' },
  { name: 'Greece',                cc: '30' },
  { name: 'Czech Republic',        cc: '420' },
  { name: 'Mexico',                cc: '52' },
  { name: 'Brazil',                cc: '55' },
  { name: 'Argentina',             cc: '54' },
  { name: 'Chile',                 cc: '56' },
  { name: 'Colombia',              cc: '57' },
  { name: 'India',                 cc: '91' },
  { name: 'Japan',                 cc: '81' },
  { name: 'South Korea',           cc: '82' },
  { name: 'China',                 cc: '86' },
  { name: 'Hong Kong',             cc: '852' },
  { name: 'Singapore',             cc: '65' },
  { name: 'Thailand',              cc: '66' },
  { name: 'Vietnam',               cc: '84' },
  { name: 'Malaysia',              cc: '60' },
  { name: 'Indonesia',             cc: '62' },
  { name: 'Philippines',           cc: '63' },
  { name: 'New Zealand',           cc: '64' },
  { name: 'South Africa',          cc: '27' },
  { name: 'Israel',                cc: '972' },
  { name: 'United Arab Emirates',  cc: '971' },
  { name: 'Saudi Arabia',          cc: '966' },
  { name: 'Turkey',                cc: '90' },
  { name: 'Russia',                cc: '7' },
];

export function E164() {
  const qc = useQueryClient();
  const rawCfg = useQuery<any>({ queryKey: ['e164'], queryFn: () => api.advanced.e164Get() }).data;
  const cfg: any = rawCfg && typeof rawCfg === 'object' && !Array.isArray(rawCfg) ? rawCfg : {};

  const [form, setForm] = useState<any>({});
  const f = {
    process_enabled:       cfg.process_enabled !== false,
    country:               'United States',
    country_code:          '1',
    international_prefix:  '011',
    remove_if_same_country: cfg.remove_if_same_country !== false,
    area_code:             '',
    national_prefix:       '',
    remove_if_same_area:   false,
    add_prefix:            '',
    ...cfg,
    ...form,
  };
  const set = (k: string, v: any) => setForm({ ...form, [k]: v });

  const save = useMutation({
    mutationFn: () => api.advanced.e164Set(f),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['e164'] }),
  });

  // Country picker mirrors "United States (+1)" pattern; selecting one
  // sets both name and country_code in one shot.
  const countryValue = `${f.country}|${f.country_code}`;
  function pickCountry(v: string) {
    const [name, cc] = v.split('|');
    setForm({ ...form, country: name, country_code: cc });
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold flex-1">E164 processing settings</h1>
        <button className="btn-primary text-sm inline-flex items-center gap-1" onClick={() => save.mutate()}>
          <Save size={14} /> Save
        </button>
      </div>

      <div className="card p-4 space-y-4">
        <label className="inline-flex items-center gap-2 text-sm font-medium">
          <input type="checkbox" checked={!!f.process_enabled}
                 onChange={e => set('process_enabled', e.target.checked)} />
          Process E164 numbers
        </label>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Select country *">
            <select className="input" value={countryValue} onChange={e => pickCountry(e.target.value)}
                    disabled={!f.process_enabled}>
              {E164_COUNTRIES.map(c => (
                <option key={`${c.name}|${c.cc}`} value={`${c.name}|${c.cc}`}>
                  {c.name} (+{c.cc})
                </option>
              ))}
            </select>
          </Field>
          <Field label="International dial code *">
            <input className="input font-mono" value={f.international_prefix}
                   onChange={e => set('international_prefix', e.target.value)}
                   placeholder="011"
                   disabled={!f.process_enabled} />
          </Field>
        </div>

        <label className="inline-flex items-center gap-2 text-sm font-medium">
          <input type="checkbox" checked={!!f.remove_if_same_country}
                 onChange={e => set('remove_if_same_country', e.target.checked)}
                 disabled={!f.process_enabled} />
          Remove if same country
        </label>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Area code">
            <input className="input font-mono" value={f.area_code}
                   onChange={e => set('area_code', e.target.value)}
                   disabled={!f.process_enabled || !f.remove_if_same_country} />
          </Field>
          <Field label="National code">
            <input className="input font-mono" value={f.national_prefix}
                   onChange={e => set('national_prefix', e.target.value)}
                   disabled={!f.process_enabled || !f.remove_if_same_country} />
          </Field>
        </div>

        <label className="inline-flex items-center gap-2 text-sm font-medium">
          <input type="checkbox" checked={!!f.remove_if_same_area}
                 onChange={e => set('remove_if_same_area', e.target.checked)}
                 disabled={!f.process_enabled} />
          Remove if same area code
        </label>

        <Field label="Add prefix">
          <input className="input font-mono" value={f.add_prefix}
                 onChange={e => set('add_prefix', e.target.value)}
                 disabled={!f.process_enabled} />
        </Field>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// License
// ────────────────────────────────────────────────────────────────────────────
// Real License page (#1047). 3CX-style: product/version header, licensed-to,
// seat usage vs the allowance encoded in the key, expiry, per-tenant seat
// table, and a STRA-XXXX-XXXX-XXXX-XXXX key entry (super_admin). Display +
// validation only — nothing is enforced or locked out from here.
export function LicensePage() {
  const qc = useQueryClient();
  const me = api.me();
  const isAdmin = me?.role === 'super_admin';
  const { data, isLoading } = useQuery({ queryKey: ['license-full'], queryFn: () => api.advanced.licenseFull() });
  const [key, setKey] = useState('');
  const [licensedTo, setLicensedTo] = useState('');
  const apply = useMutation({
    mutationFn: () => api.advanced.licenseApply({ key: key.trim(), licensed_to: licensedTo.trim() }),
    onSuccess: () => { setKey(''); qc.invalidateQueries({ queryKey: ['license-full'] }); },
  });

  const lic = data?.license || null;
  const dec = lic?.decoded && !lic.decoded.error ? lic.decoded : null;
  const seats = data?.seats || {};
  const used = seats.total_extensions ?? 0;
  const allowance: number | null = dec ? dec.seat_allowance : null; // 0 = unlimited
  const unlimited = allowance === 0;
  const over = !unlimited && allowance != null && used > allowance;
  const pct = unlimited || !allowance ? 0 : Math.min(100, Math.round((used / allowance) * 100));
  const expiry = dec ? (dec.expires_at ? new Date(dec.expires_at).toLocaleDateString() : 'Never (perpetual)') : null;
  const tenants: any[] = seats.tenants || [];

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold flex items-center gap-2"><FileText /> License</h1>

      {/* Product + license summary */}
      <div className="card p-4 space-y-2">
        <div className="flex justify-between text-sm"><span className="text-textSecondary">Product</span><span className="font-mono">{data?.product || 'Stratus VoIP'}</span></div>
        <div className="flex justify-between text-sm"><span className="text-textSecondary">Version</span><span className="font-mono">{data?.version || '—'}</span></div>
        <div className="flex justify-between text-sm"><span className="text-textSecondary">Licensed to</span><span>{lic?.licensed_to || '—'}</span></div>
        <div className="flex justify-between text-sm"><span className="text-textSecondary">Edition</span><span className="capitalize">{dec?.edition || (lic ? lic.edition : 'Unlicensed')}</span></div>
        <div className="flex justify-between text-sm">
          <span className="text-textSecondary">Expiry</span>
          <span className={dec?.expired ? 'text-danger font-medium' : ''}>{expiry || '—'}{dec?.expired ? ' (expired)' : ''}</span>
        </div>
        {lic && (
          <>
            <div className="flex justify-between text-sm"><span className="text-textSecondary">License key</span><span className="font-mono">{lic.license_key}</span></div>
            <div className="flex justify-between text-sm"><span className="text-textSecondary">Applied</span><span>{lic.applied_at ? new Date(lic.applied_at).toLocaleString() : '—'}{lic.applied_by ? ` by ${lic.applied_by}` : ''}</span></div>
          </>
        )}
        {lic?.decoded?.error && (
          <div className="mt-1 rounded-md bg-danger/15 text-danger px-3 py-2 text-sm">Stored key no longer validates: {lic.decoded.error}</div>
        )}
        {!isLoading && !lic && (
          <div className="mt-1 rounded-md bg-warning/15 text-warning px-3 py-2 text-sm">No license key applied. Stratus keeps running — this page is informational only.</div>
        )}
      </div>

      {/* Seat usage */}
      <div className="card p-4 space-y-3">
        <div className="text-sm font-medium">Seat usage</div>
        <div className="flex justify-between text-sm">
          <span className="text-textSecondary">Extensions in use (all tenants)</span>
          <span className={over ? 'text-danger font-medium' : ''}>
            {used}{allowance != null ? ` / ${unlimited ? 'Unlimited' : allowance}` : ''}
          </span>
        </div>
        {allowance != null && !unlimited && (
          <div className="h-2 rounded-full bg-elevated overflow-hidden">
            <div className={`h-full rounded-full ${over ? 'bg-danger' : 'bg-primary'}`} style={{ width: `${pct}%` }} />
          </div>
        )}
        {over && <div className="text-xs text-danger">Seat allowance exceeded — informational only, no calls are blocked.</div>}
        {seats.total_users != null && (
          <div className="flex justify-between text-sm"><span className="text-textSecondary">User accounts</span><span>{seats.total_users}</span></div>
        )}
        <table className="w-full text-sm">
          <thead className="bg-elevated text-textSecondary text-left">
            <tr><th className="px-3 py-1.5">Tenant</th><th className="px-3 py-1.5 text-right">Extensions</th></tr>
          </thead>
          <tbody>
            {tenants.map(t => (
              <tr key={t.domain_uuid} className="border-t border-border">
                <td className="px-3 py-1.5">{t.domain_name}</td>
                <td className="px-3 py-1.5 text-right font-mono">{t.extensions}</td>
              </tr>
            ))}
            {!tenants.length && <tr><td colSpan={2} className="px-3 py-3 text-center text-textSecondary">{isLoading ? 'Loading…' : 'No tenants'}</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Key entry (super_admin) */}
      {isAdmin && (
        <div className="card p-4 space-y-3">
          <div className="text-sm font-medium">Apply a license key</div>
          <p className="text-xs text-textSecondary">Format: STRA-XXXX-XXXX-XXXX-XXXX. The key is checksum-validated and stored; edition, seat allowance and expiry are read from the key itself. No features are locked either way.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="License key">
              <input className="input font-mono uppercase" placeholder="STRA-XXXX-XXXX-XXXX-XXXX" value={key} onChange={e => setKey(e.target.value)} />
            </Field>
            <Field label="Licensed to (company name)">
              <input className="input" placeholder="Computers To Go" value={licensedTo} onChange={e => setLicensedTo(e.target.value)} />
            </Field>
          </div>
          <div className="flex items-center gap-2">
            <button className="btn-primary" disabled={apply.isPending || !key.trim()} onClick={() => apply.mutate()}>Apply Key</button>
            {apply.isSuccess && <span className="text-primary text-sm">License applied.</span>}
            {apply.isError && <span className="text-danger text-sm">{(apply.error as any)?.message || 'Invalid key'}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Updates
// ────────────────────────────────────────────────────────────────────────────
// Release & Update panel (#162). Same page on every Stratus instance: it lists the versions
// (SemVer tags in the shared Forgejo), shows what THIS box runs, and lets a super_admin pin the
// box to any version — Update forward or Rollback back. The button writes .deploy-request; the
// per-box pull-deploy agent (#161) checks out that tag and rebuilds within ~60s.
const verNums = (v: string): number[] => { const m = /v?(\d+)\.(\d+)\.(\d+)/.exec(v || ''); return m ? [+m[1], +m[2], +m[3]] : [0, 0, 0]; };
const verCmp = (a: string, b: string): number => { const A = verNums(a), B = verNums(b); for (let i = 0; i < 3; i++) if (A[i] !== B[i]) return A[i] - B[i]; return 0; };

export function UpdatesPage() {
  const qc = useQueryClient();
  const me = api.me();
  const isAdmin = me?.role === 'super_admin';
  const { data, isLoading } = useQuery({ queryKey: ['releases'], queryFn: () => api.releases.list(), refetchInterval: 15_000 });
  const prep = useMutation({
    mutationFn: (type: 'release' | 'hotfix' | 'major') => api.releases.prep(type),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['releases'] }),
  });
  const deploy = useMutation({
    mutationFn: (version: string) => api.releases.deploy(version),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['releases'] }),
  });

  const versions = data?.versions || [];
  const current = data?.current || '';
  const curTag = (current.match(/^v\d+\.\d+\.\d+/) || [])[0] || '';
  const pending = data?.pending || null;

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold flex items-center gap-2"><Rocket /> Updates</h1>

      <div className="card p-4 space-y-2">
        <div className="flex justify-between text-sm"><span className="text-textSecondary">This instance is running</span><span className="font-mono">{curTag || current || '—'}</span></div>
        <div className="flex justify-between text-sm"><span className="text-textSecondary">Latest available version</span><span className="font-mono">{data?.latest || '—'}</span></div>
        <div className="flex justify-between text-sm"><span className="text-textSecondary">Unreleased changes on master</span><span>{data?.unreleased_count ?? 0}</span></div>
        {pending && (
          <div className="mt-2 rounded-md bg-warning/15 text-warning px-3 py-2 text-sm flex items-center gap-2">
            <Activity size={14} className="animate-pulse" /> Updating to <span className="font-mono">{pending}</span>… the deploy agent applies this within ~60s, then rebuilds.
          </div>
        )}
      </div>

      {/* "Cut a new version" is a BUILD-environment action, shown only on the
          designated builder (can_prep). Production instances are consumers —
          they Update/Rollback but never mint tags (#344). */}
      {isAdmin && data?.can_prep && (
        <div className="card p-4 space-y-3">
          <div className="text-sm font-medium">Cut a new version</div>
          <p className="text-xs text-textSecondary">Mints a SemVer tag from the current reviewed master with an auto-generated changelog. <b>Release</b> = new features (minor bump); <b>Hotfix</b> = emergency fix (patch bump). Cutting here makes the version available to every instance.</p>
          <div className="flex gap-2 items-center">
            <button className="btn-primary" disabled={prep.isPending} onClick={() => prep.mutate('release')}>Prep Release</button>
            <button className="btn" disabled={prep.isPending} onClick={() => prep.mutate('hotfix')}>Prep Hotfix</button>
            {prep.isSuccess && <span className="text-primary text-sm">Cut {prep.data?.version}.</span>}
            {prep.isError && <span className="text-danger text-sm">{(prep.error as any)?.message || 'failed'}</span>}
          </div>
        </div>
      )}

      <div className="card p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-elevated text-textSecondary text-left">
            <tr><th className="px-4 py-2">Version</th><th className="px-4 py-2">Date</th><th className="px-4 py-2">Changes</th><th className="px-4 py-2 text-right">Action</th></tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={4} className="px-4 py-6 text-center text-textSecondary">Loading…</td></tr>}
            {!isLoading && versions.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-6 text-center text-textSecondary">No versions cut yet{isAdmin && data?.can_prep ? ' — hit “Prep Release” to mint v0.1.0.' : '.'}</td></tr>
            )}
            {versions.map(v => {
              const isCurrent = !!curTag && verCmp(v.version, curTag) === 0;
              const forward = curTag ? verCmp(v.version, curTag) > 0 : true;
              return (
                <tr key={v.version} className="border-t border-border">
                  <td className="px-4 py-2 font-mono whitespace-nowrap">
                    {v.version}
                    {isCurrent && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-primary/20 text-primary">running</span>}
                  </td>
                  <td className="px-4 py-2 text-textSecondary whitespace-nowrap">{(v.date || '').slice(0, 10)}</td>
                  <td className="px-4 py-2 text-textSecondary truncate max-w-[22rem]" title={v.headline}>{v.headline}</td>
                  <td className="px-4 py-2 text-right">
                    {isAdmin && !isCurrent ? (
                      <button
                        className={forward ? 'btn-primary' : 'btn'}
                        disabled={deploy.isPending || !!pending}
                        onClick={() => { if (window.confirm(`${forward ? 'Update' : 'Rollback'} this instance to ${v.version}? It rebuilds (~1–2 min); live calls are unaffected.`)) deploy.mutate(v.version); }}
                        title={forward ? `Update to ${v.version}` : `Rollback to ${v.version}`}
                      >{forward ? <><ArrowUp size={13} className="inline" /> Update</> : <><RotateCcw size={13} className="inline" /> Rollback</>}</button>
                    ) : <span className="text-textSecondary text-xs">{isCurrent ? 'current' : ''}</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {deploy.isError && <div className="text-danger text-sm">{(deploy.error as any)?.message || 'deploy failed'}</div>}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Multi Company (placeholder pointing at Reseller for super-admin)
// ────────────────────────────────────────────────────────────────────────────
export function MultiCompany() {
  const me = api.me();
  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold flex items-center gap-2"><FileText /> Multi Company</h1>
      <div className="card p-4 space-y-3">
        <p>Stratus is multi-tenant by default. Each tenant lives under <strong>Departments</strong>.</p>
        {(me?.role === 'super_admin' || me?.role === 'reseller') && (
          <p className="text-sm">Super-admin reseller view at <a href="/reseller" className="text-primary hover:underline">/reseller</a> shows all tenants across the deployment with billing + health.</p>
        )}
        <p className="text-sm text-textSecondary">There's no "switch to multi-company mode" toggle — Stratus is always multi-tenant.</p>
      </div>
    </div>
  );
}

// Shared Field
function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="text-sm font-medium">{label}</label>
      {children}
      {hint && <div className="text-xs text-textSecondary">{hint}</div>}
    </div>
  );
}
